/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { DEMO_SEED_VERSION, demoData } from "./demo-data";
import type {
  ActivityEvent,
  AppData,
  Company,
  Contact,
  Funnel,
  Lead,
  LeadFieldDefinition,
  LeadFieldOption,
  LeadFieldValue,
  LeadFormSection,
  LossReason,
  MembershipStatus,
  SavedView,
  Stage,
  Tag,
  Task,
  TaskProgress,
  Trigger,
} from "./types";
import { isDemoMode, supabase } from "./supabase";
import { loadRemoteData, remote } from "./supabase-repository";
import { showFeedback } from "./feedback";
import { STAGE_COLORS } from "./palette";
import { normalizeCatalogName, uniqueCatalogIds } from "../features/catalog/catalog";
import {
  applyCompanyUpdate,
  applyContactUpdate,
  applyCreatedLeadIdentity,
  applyLeadContacts,
  assertActiveCompany,
  assertIdentityWritable,
  canonicalLeadCreationEnabled,
  buildCreateOrReuseCompanyLeadInput,
  buildCompany,
  buildCompanyUpdate,
  buildContact,
  buildContactUpdate,
  createIdentityDemoAdapter,
  previewRelatedLeadInFunnel as previewRelatedLeadCommand,
  prepareLeadIdentity,
  validateLeadContacts,
  type CreateRelatedLeadInput,
  type IdentityDemoAdapter,
  type NewCompany,
  type NewContact,
  type RelatedLeadMutationResult,
  type RelatedLeadPreview,
  type MergeCompaniesInput,
  type MergeCompaniesMutationResult,
  type MergeCompaniesPreview,
  type ReclassifyPartyChannelInput,
  type ReclassifyPartyChannelMutationResult,
  type ReclassifyPartyChannelPreview,
  type ReviewIdentityCandidateInput,
  type ReviewIdentityCandidateMutationResult,
} from "../features/identity/commands";
import { countsAsInteraction } from "./activity";
import {
  commandError,
  createCommandContext,
  type CommandContext,
  type CommandResult,
  type CommandSuccess,
} from "./command-contract";
import {
  createTaskDemoAdapter,
  type TaskCommandState,
  type TaskCommandValue,
  type TaskDemoAdapter,
  type TaskDraftInput,
  type TaskEditInput,
} from "../features/tasks/commands";
import {
  createCommunicationDemoAdapter,
  type CommunicationCommandState,
  type CommunicationDemoAdapter,
  type ImportMessageInput,
  type ImportMessageValue,
  type LinkThreadInput,
  type LinkThreadValue,
  type QueueSendValue,
  type ArchiveTemplateInput,
  type CreateTemplateInput,
  type PublishTemplateVersionInput,
  type SaveDraftInput,
  type SaveDraftValue,
  type SaveTemplateDraftInput,
  type TemplateValue,
  type SendChannelMessageInput,
  type SendChannelMessageValue,
  type ThreadStateInput,
  type ThreadStateValue,
  type MarkChannelMessageInput,
  type DeleteChannelMessageInput,
  type ChannelMessageValue,
} from "../features/communication/commands";
import {
  AI_REGENERATION_CONTEXT_MISMATCH_MESSAGE,
  createAiDemoAdapter,
  type AiCommandState,
  type AiDemoAdapter,
  type RecordAiFeedbackInput,
  type RecordAiFeedbackValue,
  type RequestAiSuggestionInput,
  type RequestAiSuggestionValue,
} from "../features/ai-messages/commands";
import { createFakeAiProvider } from "../features/ai-messages/fake-provider";
import { createLocalGroqAiProvider, LOCAL_GROQ_AI_AVAILABLE } from "./local-ai-provider";
import {
  compileFlowDraftById,
  createFlowDemoAdapter,
  validateFlowDraftById,
  type ArchiveFlowDraftInput,
  type CreateFlowDraftInput,
  type DuplicateFlowDraftInput,
  type DuplicateFlowDraftValue,
  type FlowCommandState,
  type FlowDemoAdapter,
  type FlowDraftValue,
  type PublishFlowDraftInput,
  type PublishFlowDraftValue,
  type RestoreFlowDraftInput,
  type SaveFlowDraftInput,
} from "../features/flows/commands";
import {
  createFinanceDemoAdapter,
  type FinanceCommandState,
  type FinanceCommandValue,
  type FinanceDemoAdapter,
} from "../features/finance/commands";
import type {
  CancelContractInput,
  ContractDraftInput,
  ExtraInvoiceInput,
  GenerateInvoicesInput,
  ReceiptInput,
  RevenueGoalInput,
} from "../features/finance/schema";
import {
  createClassificationDemoAdapter,
  type ArchiveDefinitionInput,
  type ArchiveOptionInput,
  type ClassificationCommandState,
  type ClassificationCommandValue,
  type ClassificationDemoAdapter,
  type CreateDefinitionInput,
  type CreateOptionInput,
  type RemoveDefinitionInput,
  type RemoveOptionInput,
  type RenameDefinitionInput,
  type RenameOptionInput,
  type ReorderInput,
  type SetLeadValueInput,
} from "../features/classifications/commands";
import {
  createNotificationDemoAdapter,
  type MarkAllNoticesReadInput,
  type MarkNoticeReadInput,
  type NotificationCommandState,
  type NotificationCommandValue,
  type NotificationDemoAdapter,
  type ResolveNoticeInput,
} from "../features/notifications/commands";
import { buildNoticeFeed, type FeedInput, type Notice } from "../features/notifications/feed";
import {
  createPreferenceDemoAdapter,
  type PreferenceCommandState,
  type PreferenceCommandValue,
  type PreferenceDemoAdapter,
  type SetPreferenceInput,
} from "../features/preferences/commands";
import { mutedCategories, readPreference } from "../features/preferences/contracts";
import {
  createMeetingDemoAdapter,
  type CancelMeetingInput,
  type HoldMeetingInput,
  type MeetingCommandState,
  type MeetingCommandValue,
  type MeetingDemoAdapter,
  type RescheduleMeetingInput,
  type ScheduleMeetingInput,
  type UpdateMeetingInput,
} from "../features/meetings/commands";
import type { CompileFlowResult } from "../features/flows/compiler";
import type { FlowValidationResult } from "../features/flows/contracts";
import {
  FLOW_STATE_SCHEMA_VERSION,
  normalizeFlowExecutionMode,
  normalizeFunnelStructureType,
  normalizeStoredFlowActiveVersions,
  normalizeStoredFlowDrafts,
} from "../features/flows/schema";

type NewLead = Omit<
  Lead,
  "id" | "createdAt" | "lastInteractionAt" | "tagIds" | "triggerIds"
> & {
  tagIds?: string[];
  triggerIds?: string[];
  fieldValues?: Record<string, LeadFieldValue["value"]>;
};

export type AddLeadOptions = {
  // Deve sobreviver a retries do mesmo formulário, inclusive após erro de reload/foto.
  idempotencyKey?: string;
};

export type CommandOptions = {
  context?: CommandContext;
  idempotencyKey?: string;
  expectedVersion?: number;
  correlationId?: string;
  causationId?: string;
};

/** Nome historico mantido para os chamadores existentes de tarefas. */
export type TaskCommandOptions = CommandOptions;

function taskStateFromData(data: AppData): TaskCommandState {
  return {
    tasks: data.tasks,
    events: data.events,
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
    outboxJobs: data.outboxJobs ?? [],
    leadIds: data.leads.map((lead) => lead.id),
    membershipIds: data.memberships.map((membership) => membership.id),
  };
}

function taskStateIntoData(data: AppData, taskState: TaskCommandState): AppData {
  return {
    ...data,
    tasks: taskState.tasks,
    events: taskState.events,
    commandReceipts: taskState.commandReceipts,
    auditLog: taskState.auditLog,
    outboxJobs: taskState.outboxJobs,
  };
}

function classificationStateFromData(data: AppData): ClassificationCommandState {
  return {
    definitions: data.classificationDefinitions ?? [],
    options: data.classificationOptions ?? [],
    values: data.leadClassificationValues ?? [],
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
    leadIds: data.leads.map((lead) => lead.id),
  };
}

function classificationStateIntoData(
  data: AppData,
  state: ClassificationCommandState,
): AppData {
  return {
    ...data,
    classificationDefinitions: state.definitions,
    classificationOptions: state.options,
    leadClassificationValues: state.values,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
  };
}

function financeStateFromData(data: AppData): FinanceCommandState {
  return {
    contracts: data.contracts ?? [],
    invoices: data.invoices ?? [],
    receipts: data.receipts ?? [],
    revenueGoals: data.revenueGoals ?? [],
    events: data.events,
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
    leads: data.leads,
    memberships: data.memberships,
  };
}

function financeStateIntoData(data: AppData, financeState: FinanceCommandState): AppData {
  return {
    ...data,
    contracts: financeState.contracts,
    invoices: financeState.invoices,
    receipts: financeState.receipts,
    revenueGoals: financeState.revenueGoals,
    events: financeState.events,
    commandReceipts: financeState.commandReceipts,
    auditLog: financeState.auditLog,
    // O recebimento que fecha a venda também converte o lead e encerra a
    // participação: as três mudanças voltam juntas ou nenhuma volta.
    leads: financeState.leads,
    memberships: financeState.memberships,
  };
}

function notificationStateFromData(data: AppData): NotificationCommandState {
  return {
    states: data.notificationStates ?? [],
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
  };
}

function notificationStateIntoData(
  data: AppData,
  state: NotificationCommandState,
): AppData {
  return {
    ...data,
    notificationStates: state.states,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
  };
}

function preferenceStateFromData(data: AppData): PreferenceCommandState {
  return {
    preferences: data.userPreferences ?? [],
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
  };
}

function preferenceStateIntoData(data: AppData, state: PreferenceCommandState): AppData {
  return {
    ...data,
    userPreferences: state.preferences,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
  };
}

function meetingStateFromData(data: AppData): MeetingCommandState {
  return {
    meetings: data.meetings ?? [],
    events: data.events,
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
    leads: data.leads,
    membershipIds: data.memberships.map((membership) => membership.id),
  };
}

function meetingStateIntoData(data: AppData, state: MeetingCommandState): AppData {
  return {
    ...data,
    meetings: state.meetings,
    events: state.events,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
    // Confirmar a realização grava a interação no lead junto com o evento.
    leads: state.leads,
  };
}

function communicationStateFromData(data: AppData): CommunicationCommandState {
  return {
    mailboxes: data.mailboxes ?? [],
    mailboxCursors: data.mailboxCursors ?? [],
    threads: data.communicationThreads ?? [],
    messages: data.communicationMessages ?? [],
    deliveries: data.messageDeliveries ?? [],
    messageAttachments: data.messageAttachments ?? [],
    messageTemplates: data.messageTemplates ?? [],
    messageTemplateVersions: data.messageTemplateVersions ?? [],
    channelSuppressions: data.channelSuppressions ?? [],
    outboxJobs: data.outboxJobs ?? [],
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
    events: data.events,
    tasks: data.tasks,
    leads: data.leads,
    companies: data.companies,
    contacts: data.contacts,
    partyChannels: data.partyChannels,
    memberships: data.memberships,
  };
}

function communicationStateIntoData(
  data: AppData,
  state: CommunicationCommandState,
): AppData {
  return {
    ...data,
    mailboxes: state.mailboxes,
    mailboxCursors: state.mailboxCursors,
    communicationThreads: state.threads,
    communicationMessages: state.messages,
    messageDeliveries: state.deliveries,
    messageAttachments: state.messageAttachments,
    messageTemplates: state.messageTemplates,
    messageTemplateVersions: state.messageTemplateVersions,
    channelSuppressions: state.channelSuppressions,
    outboxJobs: state.outboxJobs,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
    // A importacao de uma resposta humana vinculada atualiza a ultima
    // interacao real do Lead no mesmo commit do comando.
    events: state.events,
    tasks: state.tasks,
    leads: state.leads,
  };
}

function aiStateFromData(data: AppData): AiCommandState {
  return {
    ownerId: data.demoOwnerId ?? "demo-owner",
    threads: data.communicationThreads ?? [],
    messages: data.communicationMessages ?? [],
    leads: data.leads,
    companies: data.companies,
    contacts: data.contacts,
    partyChannels: data.partyChannels,
    memberships: data.memberships,
    funnels: data.funnels,
    stages: data.stages,
    events: data.events,
    tasks: data.tasks,
    channelSuppressions: data.channelSuppressions ?? [],
    aiPromptVersions: data.aiPromptVersions ?? [],
    replyClassificationVersions: data.replyClassificationVersions ?? [],
    approachTypeVersions: data.approachTypeVersions ?? [],
    aiRequests: data.aiRequests ?? [],
    aiResults: data.aiResults ?? [],
    aiFeedback: data.aiFeedback ?? [],
    aiBudgetPeriods: data.aiBudgetPeriods ?? [],
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
  };
}

function aiStateIntoData(data: AppData, state: AiCommandState): AppData {
  return {
    ...data,
    aiRequests: state.aiRequests,
    aiResults: state.aiResults,
    aiFeedback: state.aiFeedback,
    aiBudgetPeriods: state.aiBudgetPeriods,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
    // Eventos de IA entram na jornada com countsAsInteraction=false; o comando
    // nunca toca leads nem last_interaction_at.
    events: state.events,
  };
}

function flowStateFromData(data: AppData): FlowCommandState {
  const catalogEntry = <T extends { id: string; funnelId: string; archived?: boolean }>(item: T) => ({
    id: item.id,
    funnelId: item.funnelId,
    archived: item.archived,
  });
  const publishedArtifact = <
    T extends { id: string; ownerId: string; version: number; status: "draft" | "published" },
  >(
    item: T,
  ) => ({ id: item.id, ownerId: item.ownerId, version: item.version, status: item.status });
  return {
    ownerId: data.demoOwnerId ?? "demo-owner",
    funnels: data.funnels.map((funnel) => ({
      id: funnel.id,
      structureType: funnel.structureType,
      defaultExecutionMode: funnel.defaultExecutionMode,
      archived: funnel.archived,
    })),
    stages: data.stages.map(catalogEntry),
    leadFields: data.leadFields.map(catalogEntry),
    tags: data.tags.map(catalogEntry),
    triggers: data.triggers.map(catalogEntry),
    lossReasons: data.lossReasons.map(catalogEntry),
    messageTemplateVersions: (data.messageTemplateVersions ?? []).map((version) => ({
      ...publishedArtifact(version),
      templateId: version.templateId,
    })),
    aiPromptVersions: (data.aiPromptVersions ?? []).map(publishedArtifact),
    replyClassificationVersions: (data.replyClassificationVersions ?? []).map((version) => ({
      ...publishedArtifact(version),
      classifications: [...version.classifications],
    })),
    approachTypeVersions: (data.approachTypeVersions ?? []).map((version) => ({
      ...publishedArtifact(version),
      approaches: version.approaches.map((approach) => ({ ...approach })),
    })),
    flowVersions: data.flowVersions ?? [],
    flowDrafts: data.flowDrafts ?? [],
    activeFlowVersions: data.flowActiveVersions ?? [],
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
  };
}

function flowStateIntoData(data: AppData, state: FlowCommandState): AppData {
  return {
    ...data,
    // Definicao/publicacao de flow nao cria evento, participacao, visita,
    // tarefa ou mensagem: somente artefatos, referencia ativa, auditoria e
    // recibo mudam.
    flowDrafts: state.flowDrafts,
    flowVersions: state.flowVersions,
    flowActiveVersions: state.activeFlowVersions,
    commandReceipts: state.commandReceipts,
    auditLog: state.auditLog,
  };
}

/** Paleta das etapas novas, na mesma ordem usada por `addStage`. */


type Store = AppData & {
  /** Estado honesto da geração manual; não implica envio nem capability remota. */
  aiProviderMode: "simulated" | "local_groq";
  moveMembership: (membershipId: string, stageId: string) => void;
  reorderMembership: (
    membershipId: string,
    stageId: string,
    targetIds: string[],
    sourceIds: string[],
  ) => void;
  setMembershipOutcome: (
    membershipId: string,
    status: MembershipStatus,
    lossReasonId?: string,
  ) => void;
  deleteJourneyEvent: (eventId: string) => void;
  toggleTask: (taskId: string, options?: TaskCommandOptions) => Promise<CommandResult<TaskCommandValue>>;
  completeTask: (taskId: string, options?: TaskCommandOptions) => Promise<CommandResult<TaskCommandValue>>;
  reopenTask: (taskId: string, options?: TaskCommandOptions) => Promise<CommandResult<TaskCommandValue>>;
  /** Muda o andamento de uma tarefa pendente (quadro de Tarefas). */
  setTaskProgress: (
    taskId: string,
    progress: TaskProgress,
    options?: TaskCommandOptions,
  ) => Promise<CommandResult<TaskCommandValue>>;
  /** Edita descrição, prazo, prioridade e observação; o cadastro não muda. */
  /** Exclui a tarefa: vira marca na jornada e some das listas. Só no demo. */
  deleteTask: (taskId: string, options?: TaskCommandOptions) => Promise<CommandResult<TaskCommandValue>>;
  updateTask: (
    input: TaskEditInput,
    options?: TaskCommandOptions,
  ) => Promise<CommandResult<TaskCommandValue>>;
  addNote: (leadId: string, text: string) => void;
  addLead: (
    lead: NewLead,
    funnelId: string,
    stageId: string,
    initialNote?: string,
    options?: AddLeadOptions,
  ) => Promise<string>;
  previewRelatedLeadInFunnel: (
    sourceLeadId: string,
    targetFunnelId: string,
  ) => Promise<RelatedLeadPreview | undefined>;
  createRelatedLeadInFunnel: (
    input: CreateRelatedLeadInput,
  ) => Promise<RelatedLeadMutationResult | undefined>;
  previewReclassifyPartyChannel: (
    channelId: string,
    targetContactId?: string | null,
  ) => Promise<ReclassifyPartyChannelPreview | undefined>;
  reclassifyPartyChannel: (
    input: ReclassifyPartyChannelInput,
  ) => Promise<ReclassifyPartyChannelMutationResult | undefined>;
  reviewIdentityCandidate: (
    input: ReviewIdentityCandidateInput,
  ) => Promise<ReviewIdentityCandidateMutationResult | undefined>;
  previewMergeCompanies: (
    sourceCompanyId: string,
    targetCompanyId: string,
  ) => Promise<MergeCompaniesPreview | undefined>;
  mergeCompanies: (
    input: MergeCompaniesInput,
  ) => Promise<MergeCompaniesMutationResult | undefined>;
  addCompany: (company: NewCompany) => Promise<string>;
  updateCompany: (companyId: string, patch: Partial<Company>) => Promise<boolean>;
  addContact: (contact: NewContact) => Promise<string>;
  updateContact: (contactId: string, patch: Partial<Contact>) => Promise<boolean>;
  setLeadContacts: (leadId: string, contactIds: string[], primaryContactId: string | null) => Promise<boolean>;
  setLeadPhoto: (leadId: string, photo: string | File) => Promise<boolean>;
  updateLead: (leadId: string, patch: Partial<Lead>) => Promise<boolean>;
  saveLeadFieldValues: (
    leadId: string,
    values: Record<string, LeadFieldValue["value"]>,
  ) => Promise<boolean>;
  addFormSection: (funnelId: string, name: string) => Promise<boolean>;
  updateFormSection: (sectionId: string, patch: Partial<LeadFormSection>) => Promise<boolean>;
  reorderFormSections: (funnelId: string, orderedIds: string[]) => Promise<boolean>;
  addLeadField: (field: Omit<LeadFieldDefinition, "id" | "order">) => Promise<boolean>;
  updateLeadField: (fieldId: string, patch: Partial<LeadFieldDefinition>) => Promise<boolean>;
  reorderLeadFields: (sectionId: string, orderedIds: string[]) => Promise<boolean>;
  addLeadFieldOption: (fieldId: string, name: string, color?: string) => Promise<string>;
  archiveLeadField: (fieldId: string, archived?: boolean) => Promise<boolean>;
  renameStage: (stageId: string, name: string) => Promise<boolean>;
  /** Nome e descrição do funil, aguardando o remoto antes do estado local (§18.1). */
  updateFunnel: (funnelId: string, patch: { name: string; description: string }) => Promise<boolean>;
  updateConfigColor: (
    kind: "stage" | "tag" | "trigger",
    id: string,
    color: string,
  ) => Promise<boolean>;
  reorderConfig: (
    kind: "funnel" | "stage" | "tag" | "trigger" | "lossReason",
    orderedIds: string[],
    parentId?: string,
  ) => Promise<boolean>;
  archiveLead: (leadId: string) => void;
  restoreLead: (leadId: string) => void;
  deleteLeadPermanently: (leadId: string) => Promise<boolean>;
  saveEmailDraft: (input: SaveDraftInput, options?: CommandOptions) => Promise<CommandResult<SaveDraftValue>>;
  queueEmailSend: (
    draftId: string,
    options?: CommandOptions & { sentInRun?: number },
  ) => Promise<CommandResult<QueueSendValue>>;
  linkEmailThread: (input: LinkThreadInput, options?: CommandOptions) => Promise<CommandResult<LinkThreadValue>>;
  transitionEmailThread: (input: ThreadStateInput, options?: CommandOptions) => Promise<CommandResult<ThreadStateValue>>;
  /** Envio nos canais de chat; o e-mail continua passando por rascunho e fila. */
  sendChannelMessage: (
    input: SendChannelMessageInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<SendChannelMessageValue>>;
  /** Marcas da mensagem dentro da conversa: favorita e fixada. */
  markChannelMessage: (
    input: MarkChannelMessageInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ChannelMessageValue>>;
  /** Exclui o conteúdo da mensagem preservando o registro na conversa. */
  deleteChannelMessage: (
    input: DeleteChannelMessageInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ChannelMessageValue>>;
  /** Cadastro de modelos: rascunho editável, versão publicada imutável. */
  createMessageTemplate: (
    input: CreateTemplateInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<TemplateValue>>;
  saveMessageTemplateDraft: (
    input: SaveTemplateDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<TemplateValue>>;
  publishMessageTemplateVersion: (
    input: PublishTemplateVersionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<TemplateValue>>;
  archiveMessageTemplate: (
    input: ArchiveTemplateInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<TemplateValue>>;
  importEmailMessage: (input: ImportMessageInput, options?: CommandOptions) => Promise<CommandResult<ImportMessageValue>>;
  requestAiSuggestion: (
    input: RequestAiSuggestionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<RequestAiSuggestionValue>>;
  recordAiFeedback: (
    input: RecordAiFeedbackInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<RecordAiFeedbackValue>>;
  addTask: (task: TaskDraftInput, options?: TaskCommandOptions) => Promise<CommandResult<TaskCommandValue>>;
  createTask: (task: TaskDraftInput, options?: TaskCommandOptions) => Promise<CommandResult<TaskCommandValue>>;
  addEvent: (
    leadId: string,
    title: string,
    type?: string,
    description?: string,
  ) => void;
  addAttachment: (leadId: string, file: File) => Promise<boolean>;
  removeAttachment: (attachmentId: string) => Promise<boolean>;
  addLink: (leadId: string, label: string, url: string) => void;
  addSavedView: (view: Omit<SavedView, "id">) => void;
  deleteSavedView: (viewId: string) => void;
  addFunnel: (name: string, description: string) => Promise<boolean>;
  /** Funil e esteira inicial numa única operação; devolve o id criado ou `false`. */
  createFunnel: (input: { name: string; description: string; stages: string[] }) => Promise<string | false>;
  /**
   * Esteira completa do funil: renomeia, cria, reordena e arquiva etapas
   * removidas. Remoto antes do local; falha mantém o estado anterior (§18.1).
   */
  saveFunnelStages: (
    funnelId: string,
    input: { rows: Array<{ id?: string; name: string }>; removedIds: string[] },
  ) => Promise<boolean>;
  duplicateFunnel: (sourceFunnelId: string, name: string) => Promise<boolean>;
  addStage: (funnelId: string, name: string) => Promise<boolean>;
  addTag: (name: string, color?: string, funnelId?: string) => Promise<boolean>;
  createTag: (name: string, color?: string, funnelId?: string) => Promise<string>;
  addTrigger: (name: string, color?: string, funnelId?: string) => Promise<boolean>;
  createTrigger: (name: string, color?: string, funnelId?: string) => Promise<string>;
  addLossReason: (name: string, funnelId?: string) => Promise<boolean>;
  renameConfig: (
    kind: "tag" | "trigger" | "lossReason",
    id: string,
    name: string,
  ) => Promise<boolean>;
  addMembership: (
    leadId: string,
    funnelId: string,
    stageId: string,
  ) => Promise<boolean>;
  toggleArchivedConfig: (
    kind: "funnel" | "stage" | "tag" | "trigger" | "lossReason",
    id: string,
  ) => Promise<boolean>;
  removeConfig: (
    kind: "funnel" | "stage" | "tag" | "trigger" | "lossReason",
    id: string,
  ) => Promise<boolean>;
  createFlowDraft: (
    input: CreateFlowDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FlowDraftValue>>;
  saveFlowDraft: (
    input: SaveFlowDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FlowDraftValue>>;
  publishFlowDraft: (
    input: PublishFlowDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<PublishFlowDraftValue>>;
  /**
   * Pausa ou retoma as inscrições do processo publicado. Pausar não desativa a
   * versão nem encerra percursos: é o controle de entrada do funil (§27.9).
   */
  setFlowEnrollmentPaused: (flowId: string, paused: boolean) => boolean;
  duplicateFlowDraft: (
    input: DuplicateFlowDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<DuplicateFlowDraftValue>>;
  archiveFlowDraft: (
    input: ArchiveFlowDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FlowDraftValue>>;
  restoreFlowDraft: (
    input: RestoreFlowDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FlowDraftValue>>;
  /** Classificações: catálogo do dono, com arquivamento e exclusão nominal. */
  createClassification: (
    input: CreateDefinitionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  renameClassification: (
    input: RenameDefinitionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  archiveClassification: (
    input: ArchiveDefinitionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  removeClassification: (
    input: RemoveDefinitionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  createClassificationOption: (
    input: CreateOptionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  renameClassificationOption: (
    input: RenameOptionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  archiveClassificationOption: (
    input: ArchiveOptionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  removeClassificationOption: (
    input: RemoveOptionInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  reorderClassifications: (
    input: ReorderInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  setLeadClassification: (
    input: SetLeadValueInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<ClassificationCommandValue>>;
  /**
   * Feed corrente de avisos, derivado do domínio e filtrado pelas preferências.
   * Passe `includeResolved` para a aba de histórico da central.
   */
  noticeFeed: (options?: { includeResolved?: boolean }) => Notice[];
  markNoticeRead: (
    input: MarkNoticeReadInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<NotificationCommandValue>>;
  resolveNotice: (
    input: ResolveNoticeInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<NotificationCommandValue>>;
  markAllNoticesRead: (
    input: MarkAllNoticesReadInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<NotificationCommandValue>>;
  setPreference: (
    input: SetPreferenceInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<PreferenceCommandValue>>;
  scheduleMeeting: (
    input: ScheduleMeetingInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<MeetingCommandValue>>;
  rescheduleMeeting: (
    input: RescheduleMeetingInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<MeetingCommandValue>>;
  updateMeeting: (
    input: UpdateMeetingInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<MeetingCommandValue>>;
  /** Confirma a realização: registra o evento e a interação real do lead. */
  holdMeeting: (
    input: HoldMeetingInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<MeetingCommandValue>>;
  cancelMeeting: (
    input: CancelMeetingInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<MeetingCommandValue>>;
  createContract: (
    input: ContractDraftInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FinanceCommandValue>>;
  generateContractInvoices: (
    input: GenerateInvoicesInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FinanceCommandValue>>;
  addExtraInvoice: (
    input: ExtraInvoiceInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FinanceCommandValue>>;
  /**
   * Registra um recebimento. O primeiro recebimento da primeira parcela também
   * converte o lead em cliente e fecha a participação, na mesma operação.
   */
  registerReceipt: (
    input: ReceiptInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FinanceCommandValue>>;
  cancelContract: (
    input: CancelContractInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FinanceCommandValue>>;
  setRevenueGoal: (
    input: RevenueGoalInput,
    options?: CommandOptions,
  ) => Promise<CommandResult<FinanceCommandValue>>;
  /** Leitura pura: valida sem alterar o rascunho nem persistir nada. */
  validateFlowDraft: (draftId: string) => FlowValidationResult | null;
  /** Leitura pura: compila o plano canonico sem publicar versao. */
  compileFlowDraft: (draftId: string) => CompileFlowResult | null;
  resetDemo: () => void;
};

const StoreContext = createContext<Store | null>(null);
const KEY = "atlas-crm-demo-v1";


/**
 * Dia civil corrente em `America/Sao_Paulo`.
 *
 * Vencimento e recebimento são datas do calendário do usuário, não instantes:
 * às 21h de Recife o dia UTC já virou, e usar `toISOString()` faria o sistema
 * recusar um recebimento de hoje por estar "no futuro" (AGENTS §7.6).
 */
const SAO_PAULO_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function currentCalendarDate(): string {
  return SAO_PAULO_DATE.format(new Date());
}

function hydrateLegacyTriggers(data: AppData): AppData {
  const defaultFunnelId = data.funnels.find((funnel) => !funnel.archived)?.id ?? data.funnels[0]?.id ?? "";
  const existingTriggers = [...(data.triggers ?? [])].map((trigger) => ({
    ...trigger,
    funnelId: trigger.funnelId ?? defaultFunnelId,
  }));
  const byName = new Map(existingTriggers.map((item) => [normalizeCatalogName(item.name), item]));
  const leads = data.leads.map((lead) => {
    const legacy = lead as Lead & { possibleTriggers?: unknown };
    const legacyNames = Array.isArray(legacy.possibleTriggers) ? legacy.possibleTriggers : [];
    const triggerIds = [...(lead.triggerIds ?? [])];
    for (const rawName of legacyNames) {
      if (typeof rawName !== "string") continue;
      const name = rawName.trim().replace(/\s+/g, " ");
      const normalized = normalizeCatalogName(name);
      if (!normalized) continue;
      let item = byName.get(normalized);
      if (!item) {
        item = {
          id: crypto.randomUUID(),
          funnelId: defaultFunnelId,
          name,
          color: "#f5b860",
          order: existingTriggers.length + 1,
        };
        existingTriggers.push(item);
        byName.set(normalized, item);
      }
      triggerIds.push(item.id);
    }
    const withoutLegacy = { ...legacy };
    delete (withoutLegacy as { possibleTriggers?: unknown }).possibleTriggers;
    return { ...withoutLegacy, triggerIds: uniqueCatalogIds(triggerIds) };
  });
  return { ...data, triggers: existingTriggers, leads };
}

/**
 * Reconcilia o estado demo com o domínio Financeiro.
 *
 * Um estado salvo antes deste domínio existir traz leads sem conversão, mas o
 * merge com `demoData` já injeta a amostra de recebimentos: o resultado seria
 * uma venda fechada sem cliente na tela. Quem manda é o recebimento — se existe
 * um recebimento de primeira venda para o lead, o lead é cliente desde aquela
 * data. Derivar do próprio estado, em vez da fixture, também conserta um estado
 * salvo pela metade (AGENTS §8).
 */
function hydrateDemoSeed(data: AppData): AppData {
  if ((data.demoSeedVersion ?? 1) >= DEMO_SEED_VERSION) return { ...data, demoSeedVersion: DEMO_SEED_VERSION };
  const funnelIds = new Set(data.funnels.map((funnel) => funnel.id));
  const stageIds = new Set(data.stages.map((stage) => stage.id));
  // O percurso de amostra de uma geração anterior é substituído pelo atual; o
  // que o usuário criou fica intacto, porque não usa o prefixo das fixtures.
  const kept = (data.flowDrafts ?? []).filter((draft) => !draft.id.startsWith("flow-demo-"));
  const taken = new Set(kept.map((draft) => draft.funnelId));
  /**
   * O percurso demo só entra quando o estado salvo ainda tem o funil e as
   * etapas que ele referencia, e quando aquele funil não tem definição
   * própria: rascunho apontando para configuração inexistente seria lixo, e
   * sobrescrever o do usuário seria perda (§8).
   */
  const seeded = (demoData.flowDrafts ?? []).filter(
    (draft) =>
      funnelIds.has(draft.funnelId) &&
      !taken.has(draft.funnelId) &&
      draft.nodes.every((node) => node.config.type !== "stage" || stageIds.has(node.config.stageId)),
  );
  return {
    ...data,
    demoSeedVersion: DEMO_SEED_VERSION,
    flowDrafts: [...kept, ...seeded],
    // Coleções que já existiam e ganharam fixtures novas no porte. O merge de
    // carga usa `saved ?? fixture`, que nunca completa uma lista salva: quem
    // tinha só as conversas de e-mail ficava com Mensagens vazia depois de um
    // canal novo entrar. Aqui a fixture volta por id, sem tocar no que o
    // usuário criou nem desfazer lixeira, leitura ou rascunho (§8).
    mailboxes: withMissingFixtures(data.mailboxes, demoData.mailboxes),
    communicationThreads: withMissingFixtures(
      data.communicationThreads,
      demoData.communicationThreads,
    ),
    communicationMessages: withMissingFixtures(
      data.communicationMessages,
      demoData.communicationMessages,
    ),
    messageDeliveries: withMissingFixtures(data.messageDeliveries, demoData.messageDeliveries),
    tasks: withMissingFixtures(data.tasks, demoData.tasks),
    events: withMissingFixtures(data.events, demoData.events),
  };
}

/** Acrescenta as fixtures ausentes por id, preservando o registro salvo. */
function withMissingFixtures<T extends { id: string }>(
  saved: readonly T[] | undefined,
  fixture: readonly T[] | undefined,
): T[] {
  const current = [...(saved ?? [])];
  const known = new Set(current.map((item) => item.id));
  for (const item of fixture ?? []) if (!known.has(item.id)) current.push(item);
  return current;
}

function hydrateDemoFinance(data: AppData): AppData {
  const contracts = data.contracts ?? demoData.contracts;
  const invoices = data.invoices ?? demoData.invoices;
  const receipts = data.receipts ?? demoData.receipts;
  const revenueGoals = data.revenueGoals ?? demoData.revenueGoals;
  const firstSales = new Map(
    (receipts ?? []).filter((receipt) => receipt.firstSale).map((receipt) => [receipt.leadId, receipt]),
  );
  return {
    ...data,
    contracts,
    invoices,
    receipts,
    revenueGoals,
    leads: data.leads.map((lead) => {
      if (lead.clientSince) return lead;
      const receipt = firstSales.get(lead.id);
      return receipt
        ? { ...lead, clientSince: receipt.receivedOn, convertedByReceiptId: receipt.id }
        : lead;
    }),
  };
}

function normalizeTask(task: Task): Task {
  const legacy = task as Task & {
    createdAt?: string;
    updatedAt?: string;
    version?: number;
    origin?: Task["origin"];
    actionType?: Task["actionType"];
    sensitive?: boolean;
  };
  const createdAt = legacy.createdAt ?? task.completedAt ?? task.dueAt;
  return {
    ...task,
    description: task.description.trim(),
    createdAt,
    updatedAt: legacy.updatedAt ?? createdAt,
    version: legacy.version ?? 1,
    progress: task.progress ?? "todo",
    origin: legacy.origin ?? "human",
    actionType: legacy.actionType ?? "manual_follow_up",
    sensitive: legacy.sensitive ?? false,
  };
}

function hydrateLegacyThreadLeadCompanies(data: AppData): Lead[] {
  const companyByLeadId = new Map<string, string>();
  const ambiguousLeadIds = new Set<string>();

  for (const thread of data.communicationThreads ?? []) {
    if (
      thread.linkState !== "linked" ||
      !thread.leadId ||
      !thread.companyId ||
      !thread.contactId ||
      !thread.funnelId
    )
      continue;

    const hasCompany = data.companies.some((company) => company.id === thread.companyId);
    const hasMembership = data.memberships.some(
      (membership) => membership.leadId === thread.leadId && membership.funnelId === thread.funnelId,
    );
    const hasContact = data.contacts.some(
      (contact) => contact.id === thread.contactId && contact.companyId === thread.companyId,
    );
    const hasLeadContact = data.leadContacts.some(
      (leadContact) => leadContact.leadId === thread.leadId && leadContact.contactId === thread.contactId,
    );
    if (!hasCompany || !hasMembership || !hasContact || !hasLeadContact) continue;

    const knownCompanyId = companyByLeadId.get(thread.leadId);
    if (knownCompanyId && knownCompanyId !== thread.companyId) {
      ambiguousLeadIds.add(thread.leadId);
      companyByLeadId.delete(thread.leadId);
      continue;
    }
    if (!ambiguousLeadIds.has(thread.leadId)) companyByLeadId.set(thread.leadId, thread.companyId);
  }

  return data.leads.map((lead) => {
    if (lead.companyId || ambiguousLeadIds.has(lead.id)) return lead;
    const companyId = companyByLeadId.get(lead.id);
    return companyId ? { ...lead, companyId } : lead;
  });
}

function normalizeTransversalData(data: AppData, demoOwnerId: string): AppData {
  const owner = data.demoOwnerId ?? demoOwnerId;
  // As fixtures de comunicação nascem com um owner fixo; em demo elas passam a
  // pertencer ao proprietário sorteado da sessão para que os comandos validem
  // ownership do mesmo jeito que validariam no Supabase.
  const withOwner = <T extends { ownerId: string }>(items: T[] | undefined): T[] =>
    (items ?? []).map((item) => (item.ownerId === owner ? item : { ...item, ownerId: owner }));
  const communicationThreads = withOwner(data.communicationThreads);
  const aiRequests = withOwner(data.aiRequests).map((request) => ({
    ...request,
    mailboxId:
      request.mailboxId ?? communicationThreads.find((thread) => thread.id === request.threadId)?.mailboxId ?? "",
  }));
  const requestById = new Map(aiRequests.map((request) => [request.id, request]));
  const aiResults = withOwner(data.aiResults).map((result) => ({
    ...result,
    supersedesRequestId:
      result.supersedesRequestId ?? requestById.get(result.requestId)?.regeneratedFromRequestId,
  }));
  const aiFeedback = withOwner(data.aiFeedback).map((feedback) => {
    const request = requestById.get(feedback.requestId);
    return {
      ...feedback,
      threadId: feedback.threadId ?? request?.threadId ?? "",
      mailboxId: feedback.mailboxId ?? request?.mailboxId ?? "",
    };
  });
  const flowVersions = withOwner(data.flowVersions);
  return {
    ...data,
    demoOwnerId: owner,
    // Funil legado nao possui decisao persistida de estrutura: ele e lido como
    // simple sem reinterpretar etapas, visitas, ordenacao ou analytics.
    funnels: data.funnels.map((funnel) => ({
      ...funnel,
      structureType: normalizeFunnelStructureType(funnel.structureType),
      defaultExecutionMode: normalizeFlowExecutionMode(funnel.defaultExecutionMode),
    })),
    flowSchemaVersion: FLOW_STATE_SCHEMA_VERSION,
    flowDrafts: normalizeStoredFlowDrafts(data.flowDrafts, owner),
    flowVersions,
    flowActiveVersions: normalizeStoredFlowActiveVersions(
      data.flowActiveVersions,
      owner,
      flowVersions,
    ),
    // Snapshots demo anteriores à identidade canônica podem ter o vínculo
    // completo da conversa, mas ainda não o `companyId` no Lead. A migração
    // só preenche a lacuna quando todas as relações persistidas concordam;
    // valores existentes ou vínculos ambíguos continuam falhando fechado.
    leads: hydrateLegacyThreadLeadCompanies(data),
    tasks: data.tasks.map(normalizeTask),
    events: data.events.map((event) => ({
      ...event,
      countsAsInteraction: event.countsAsInteraction ?? false,
    })),
    commandReceipts: data.commandReceipts ?? [],
    auditLog: data.auditLog ?? [],
    externalConnections: data.externalConnections ?? [],
    outboxJobs: data.outboxJobs ?? [],
    automationJobs: data.automationJobs ?? [],
    jobAttempts: data.jobAttempts ?? [],
    automationIncidents: data.automationIncidents ?? [],
    featureFlags: data.featureFlags ?? [],
    googleTaskBindings: data.googleTaskBindings ?? [],
    taskExternalLinks: data.taskExternalLinks ?? [],
    calendarReminderBindings: data.calendarReminderBindings ?? [],
    googleSyncCheckpoints: data.googleSyncCheckpoints ?? [],
    mailboxes: withOwner(data.mailboxes),
    mailboxCursors: withOwner(data.mailboxCursors),
    communicationThreads,
    communicationMessages: withOwner(data.communicationMessages),
    messageDeliveries: withOwner(data.messageDeliveries),
    messageAttachments: withOwner(data.messageAttachments),
    messageTemplates: withOwner(data.messageTemplates),
    messageTemplateVersions: withOwner(data.messageTemplateVersions),
    channelSuppressions: withOwner(data.channelSuppressions),
    aiPromptVersions: withOwner(data.aiPromptVersions),
    replyClassificationVersions: withOwner(data.replyClassificationVersions),
    approachTypeVersions: withOwner(data.approachTypeVersions),
    aiRequests,
    aiResults,
    aiFeedback,
    aiBudgetPeriods: withOwner(data.aiBudgetPeriods),
  };
}

function readInitial(): AppData {
  try {
    const raw = localStorage.getItem(KEY);
    const demoOwnerId = `demo:${crypto.randomUUID()}`;
    if (!raw) return normalizeTransversalData(demoData, demoOwnerId);
    const saved = JSON.parse(raw) as AppData;
    return normalizeTransversalData(hydrateDemoSeed(hydrateDemoFinance(hydrateLegacyTriggers({
      ...demoData,
      ...saved,
      identityCapabilityStatus: saved.identityCapabilityStatus ?? "available",
      identityCapabilitySchemaVersion: saved.identityCapabilitySchemaVersion ?? 1,
      identityCapabilityMessage: saved.identityCapabilityMessage,
      identityFoundationAvailable: saved.identityFoundationAvailable ?? true,
      canonicalLeadCreationAvailable: saved.canonicalLeadCreationAvailable ?? true,
      canonicalLeadCreationCanary: saved.canonicalLeadCreationCanary ?? false,
      emailInboxAvailable: saved.emailInboxAvailable ?? demoData.emailInboxAvailable,
      emailSendAvailable: saved.emailSendAvailable ?? false,
      aiAssistanceAvailable: saved.aiAssistanceAvailable ?? demoData.aiAssistanceAvailable,
      aiAutoSendAvailable: saved.aiAutoSendAvailable ?? false,
      flowDefinitionsAvailable: saved.flowDefinitionsAvailable ?? demoData.flowDefinitionsAvailable,
      // Estado salvo no navegador nunca liga publicação ou execução: quem
      // decide é a fixture demo (publica localmente) ou o remoto.
      flowPublicationAvailable: demoData.flowPublicationAvailable,
      flowExecutionAvailable: demoData.flowExecutionAvailable,
      // A geração é a do estado salvo, não a das fixtures: é o que permite
      // `hydrateDemoSeed` reconhecer um estado anterior e completá-lo.
      demoSeedVersion: saved.demoSeedVersion,
      // O percurso demo é decidido por `hydrateDemoSeed`, que confere funil e etapas.
      flowDrafts: saved.flowDrafts ?? [],
      flowVersions: saved.flowVersions ?? [],
      flowActiveVersions: saved.flowActiveVersions ?? [],
      mailboxes: saved.mailboxes ?? demoData.mailboxes,
      mailboxCursors: saved.mailboxCursors ?? demoData.mailboxCursors,
      communicationThreads: saved.communicationThreads ?? demoData.communicationThreads,
      communicationMessages: saved.communicationMessages ?? demoData.communicationMessages,
      messageDeliveries: saved.messageDeliveries ?? demoData.messageDeliveries,
      messageAttachments: saved.messageAttachments ?? demoData.messageAttachments,
      messageTemplates: saved.messageTemplates ?? demoData.messageTemplates,
      messageTemplateVersions: saved.messageTemplateVersions ?? demoData.messageTemplateVersions,
      channelSuppressions: saved.channelSuppressions ?? demoData.channelSuppressions,
      aiPromptVersions: saved.aiPromptVersions ?? demoData.aiPromptVersions,
      replyClassificationVersions:
        saved.replyClassificationVersions ?? demoData.replyClassificationVersions,
      approachTypeVersions: saved.approachTypeVersions ?? demoData.approachTypeVersions,
      aiRequests: saved.aiRequests ?? [],
      aiResults: saved.aiResults ?? [],
      aiFeedback: saved.aiFeedback ?? [],
      aiBudgetPeriods: saved.aiBudgetPeriods ?? demoData.aiBudgetPeriods,
      companies: saved.companies ?? [],
      contacts: saved.contacts ?? [],
      partyChannels: saved.partyChannels ?? [],
      leadContacts: saved.leadContacts ?? [],
      leadRelations: saved.leadRelations ?? [],
      identityMatchCandidates: saved.identityMatchCandidates ?? [],
      funnels: saved.funnels ?? demoData.funnels,
      stages: saved.stages ?? demoData.stages,
      memberships: saved.memberships ?? demoData.memberships,
      tasks: saved.tasks ?? demoData.tasks,
      events: saved.events ?? demoData.events,
      tags: saved.tags ?? demoData.tags,
      triggers: saved.triggers ?? demoData.triggers,
      lossReasons: saved.lossReasons ?? demoData.lossReasons,
      savedViews: saved.savedViews ?? demoData.savedViews,
      attachments: saved.attachments ?? demoData.attachments,
      links: saved.links ?? demoData.links,
      formSections: saved.formSections ?? demoData.formSections,
      leadFields: saved.leadFields ?? demoData.leadFields,
      leadFieldOptions: saved.leadFieldOptions ?? demoData.leadFieldOptions,
      leadFieldValues: saved.leadFieldValues ?? demoData.leadFieldValues,
      leads: (saved.leads ?? demoData.leads).map((lead, index) => ({
        ...lead,
        role:
          lead.role ||
          [
            "Proprietário(a)",
            "Sócio(a)",
            "Gerente de marketing",
            "Tomador(a) de decisão",
            "Gatekeeper",
          ][index % 5],
        potentialValue:
          lead.potentialValue ||
          [3500, 2800, 4200, 1800, 2400, 5000][index % 6],
      })),
    }))), saved.demoOwnerId ?? demoOwnerId);
  } catch {
    return normalizeTransversalData(demoData, `demo:${crypto.randomUUID()}`);
  }
}

function createEmptyRemoteData(): AppData {
  return {
    identityCapabilityStatus: "not_installed",
    identityCapabilitySchemaVersion: 0,
    identityFoundationAvailable: false,
    canonicalLeadCreationAvailable: false,
    canonicalLeadCreationCanary: false,
    emailInboxAvailable: false,
    emailSendAvailable: false,
    aiAssistanceAvailable: false,
    aiAutoSendAvailable: false,
    flowDefinitionsAvailable: false,
    financeAvailable: false,
    flowPublicationAvailable: false,
    flowExecutionAvailable: false,
    flowDrafts: [],
    flowVersions: [],
    flowActiveVersions: [],
    companies: [],
    contacts: [],
    partyChannels: [],
    leadContacts: [],
    leadRelations: [],
    identityMatchCandidates: [],
    commandReceipts: [],
    auditLog: [],
    externalConnections: [],
    outboxJobs: [],
    automationJobs: [],
    jobAttempts: [],
    automationIncidents: [],
    featureFlags: [],
    googleTaskBindings: [],
    taskExternalLinks: [],
    calendarReminderBindings: [],
    googleSyncCheckpoints: [],
    leads: [],
    funnels: [],
    stages: [],
    memberships: [],
    tasks: [],
    events: [],
    tags: [],
    triggers: [],
    lossReasons: [],
    savedViews: [],
    attachments: [],
    links: [],
    formSections: [],
    leadFields: [],
    leadFieldOptions: [],
    leadFieldValues: [],
  };
}

type RemoteSessionToken = {
  revision: number;
  ownerId: string | null;
};

type GuardedRemoteResult<T> =
  | { stale: true }
  | { stale: false; value: T };

export function isStaleRemoteSessionResult(result: CommandResult<unknown>): boolean {
  return !result.ok && result.error.details?.staleSession === true;
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppData>(() =>
    isDemoMode
      ? readInitial()
      : {
          identityCapabilityStatus: "not_installed",
          identityCapabilitySchemaVersion: 0,
          identityFoundationAvailable: false,
          canonicalLeadCreationAvailable: false,
          canonicalLeadCreationCanary: false,
          emailInboxAvailable: false,
          emailSendAvailable: false,
          aiAssistanceAvailable: false,
          aiAutoSendAvailable: false,
          flowDefinitionsAvailable: false,
          financeAvailable: false,
          flowPublicationAvailable: false,
          flowExecutionAvailable: false,
          flowDrafts: [],
          flowVersions: [],
          flowActiveVersions: [],
          companies: [],
          contacts: [],
          partyChannels: [],
          leadContacts: [],
          leadRelations: [],
          identityMatchCandidates: [],
          commandReceipts: [],
          auditLog: [],
          externalConnections: [],
          outboxJobs: [],
          automationJobs: [],
          jobAttempts: [],
          automationIncidents: [],
          featureFlags: [],
          googleTaskBindings: [],
          taskExternalLinks: [],
          calendarReminderBindings: [],
          googleSyncCheckpoints: [],
          leads: [],
          funnels: [],
          stages: [],
          memberships: [],
          tasks: [],
          events: [],
          tags: [],
          triggers: [],
          lossReasons: [],
          savedViews: [],
          attachments: [],
          links: [],
          formSections: [],
          leadFields: [],
          leadFieldOptions: [],
          leadFieldValues: [],
        },
  );
  const dataRef = useRef(data);
  dataRef.current = data;
  const taskAdapterRef = useRef<TaskDemoAdapter | null>(null);
  const communicationAdapterRef = useRef<CommunicationDemoAdapter | null>(null);
  const aiAdapterRef = useRef<AiDemoAdapter | null>(null);
  const flowAdapterRef = useRef<FlowDemoAdapter | null>(null);
  const financeAdapterRef = useRef<FinanceDemoAdapter | null>(null);
  const classificationAdapterRef = useRef<ClassificationDemoAdapter | null>(null);
  const notificationAdapterRef = useRef<NotificationDemoAdapter | null>(null);
  const preferenceAdapterRef = useRef<PreferenceDemoAdapter | null>(null);
  const meetingAdapterRef = useRef<MeetingDemoAdapter | null>(null);
  const identityAdapterRef = useRef<IdentityDemoAdapter | null>(null);
  const remoteRefreshSequenceRef = useRef(0);
  const remoteSessionRevisionRef = useRef(0);
  const remoteOwnerIdRef = useRef<string | null>(null);
  const authEventObservedRef = useRef(false);
  const currentRemoteSession = (): RemoteSessionToken => ({
    revision: remoteSessionRevisionRef.current,
    ownerId: remoteOwnerIdRef.current,
  });
  const isCurrentRemoteSession = (token: RemoteSessionToken) =>
    token.revision === remoteSessionRevisionRef.current &&
    token.ownerId === remoteOwnerIdRef.current;
  const clearRemoteSnapshot = () => {
    const cleared = createEmptyRemoteData();
    dataRef.current = cleared;
    setData(cleared);
  };
  if (isDemoMode && !identityAdapterRef.current) {
    identityAdapterRef.current = createIdentityDemoAdapter(data, {
      now: () => new Date().toISOString(),
      uuid: () => crypto.randomUUID(),
    });
  }
  if (isDemoMode && !taskAdapterRef.current) {
    taskAdapterRef.current = createTaskDemoAdapter(
      taskStateFromData(data),
      {
        now: () => new Date().toISOString(),
        uuid: () => crypto.randomUUID(),
      },
      (taskState) => {
        dataRef.current = taskStateIntoData(dataRef.current, taskState);
        setData((current) => taskStateIntoData(current, taskState));
      },
    );
  }
  if (isDemoMode && !communicationAdapterRef.current) {
    communicationAdapterRef.current = createCommunicationDemoAdapter(
      communicationStateFromData(data),
      {
        now: () => new Date().toISOString(),
        uuid: () => crypto.randomUUID(),
      },
      (communicationState) => {
        dataRef.current = communicationStateIntoData(dataRef.current, communicationState);
        setData((current) => communicationStateIntoData(current, communicationState));
      },
    );
  }
  if (isDemoMode && !aiAdapterRef.current) {
    const useLocalGroq = LOCAL_GROQ_AI_AVAILABLE;
    aiAdapterRef.current = createAiDemoAdapter(
      aiStateFromData(data),
      {
        now: () => new Date().toISOString(),
        uuid: () => crypto.randomUUID(),
        provider: useLocalGroq ? createLocalGroqAiProvider() : createFakeAiProvider(),
        executionMode: useLocalGroq ? "live" : "simulated",
        freeTierOnly: useLocalGroq,
      },
      (aiState) => {
        dataRef.current = aiStateIntoData(dataRef.current, aiState);
        setData((current) => aiStateIntoData(current, aiState));
      },
    );
  }
  if (isDemoMode && !flowAdapterRef.current) {
    flowAdapterRef.current = createFlowDemoAdapter(
      flowStateFromData(data),
      {
        now: () => new Date().toISOString(),
        uuid: () => crypto.randomUUID(),
      },
      (flowState) => {
        dataRef.current = flowStateIntoData(dataRef.current, flowState);
        setData((current) => flowStateIntoData(current, flowState));
      },
    );
  }
  if (isDemoMode && !classificationAdapterRef.current) {
    classificationAdapterRef.current = createClassificationDemoAdapter(
      classificationStateFromData(data),
      {
        now: () => new Date().toISOString(),
        uuid: () => crypto.randomUUID(),
      },
      (classificationState) => {
        dataRef.current = classificationStateIntoData(dataRef.current, classificationState);
        setData((current) => classificationStateIntoData(current, classificationState));
      },
    );
  }
  if (isDemoMode && !notificationAdapterRef.current) {
    notificationAdapterRef.current = createNotificationDemoAdapter(
      notificationStateFromData(data),
      { now: () => new Date().toISOString(), uuid: () => crypto.randomUUID() },
      (notificationState) => {
        dataRef.current = notificationStateIntoData(dataRef.current, notificationState);
        setData((current) => notificationStateIntoData(current, notificationState));
      },
    );
  }
  if (isDemoMode && !preferenceAdapterRef.current) {
    preferenceAdapterRef.current = createPreferenceDemoAdapter(
      preferenceStateFromData(data),
      { now: () => new Date().toISOString(), uuid: () => crypto.randomUUID() },
      (preferenceState) => {
        dataRef.current = preferenceStateIntoData(dataRef.current, preferenceState);
        setData((current) => preferenceStateIntoData(current, preferenceState));
      },
    );
  }
  if (isDemoMode && !meetingAdapterRef.current) {
    meetingAdapterRef.current = createMeetingDemoAdapter(
      meetingStateFromData(data),
      { now: () => new Date().toISOString(), uuid: () => crypto.randomUUID() },
      (meetingState) => {
        dataRef.current = meetingStateIntoData(dataRef.current, meetingState);
        setData((current) => meetingStateIntoData(current, meetingState));
      },
    );
  }
  if (isDemoMode && !financeAdapterRef.current) {
    financeAdapterRef.current = createFinanceDemoAdapter(
      financeStateFromData(data),
      {
        now: () => new Date().toISOString(),
        today: currentCalendarDate,
        uuid: () => crypto.randomUUID(),
      },
      (financeState) => {
        dataRef.current = financeStateIntoData(dataRef.current, financeState);
        setData((current) => financeStateIntoData(current, financeState));
      },
    );
  }
  useEffect(() => {
    if (isDemoMode) localStorage.setItem(KEY, JSON.stringify(data));
  }, [data]);
  useEffect(() => {
    if (isDemoMode || !supabase) return;
    let active = true;
    authEventObservedRef.current = false;
    const load = (token: RemoteSessionToken) => {
      const refreshSequence = ++remoteRefreshSequenceRef.current;
      void loadRemoteData()
        .then((next) => {
          if (!active || !isCurrentRemoteSession(token) || refreshSequence !== remoteRefreshSequenceRef.current) return;
          dataRef.current = next;
          setData(next);
        })
        .catch((error) => {
          if (!active || !isCurrentRemoteSession(token) || refreshSequence !== remoteRefreshSequenceRef.current) return;
          console.error("Falha ao carregar Supabase", error);
        });
    };
    const applyAuthSession = (session: { user?: { id?: string } } | null) => {
      if (!active) return;
      const ownerId = session?.user?.id ?? null;
      const previousOwnerId = remoteOwnerIdRef.current;
      const ownerChanged = previousOwnerId !== ownerId;
      const sessionEnded = !ownerId;

      if (!ownerChanged && !sessionEnded) {
        return;
      }

      remoteSessionRevisionRef.current += 1;
      remoteOwnerIdRef.current = ownerId;
      if (sessionEnded) remoteRefreshSequenceRef.current += 1;
      clearRemoteSnapshot();
      if (ownerId) load(currentRemoteSession());
    };
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        authEventObservedRef.current = true;
        applyAuthSession(session);
      },
    );
    void supabase.auth.getSession().then(({ data }) => {
      if (!authEventObservedRef.current) applyAuthSession(data.session);
    });
    return () => {
      active = false;
      remoteSessionRevisionRef.current += 1;
      remoteRefreshSequenceRef.current += 1;
      remoteOwnerIdRef.current = null;
      listener.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<Store>(() => {
    const guardedRemoteCall = async <T,>(
      token: RemoteSessionToken,
      operation: () => Promise<T>,
    ): Promise<GuardedRemoteResult<T>> => {
      try {
        const value = await operation();
        if (!isCurrentRemoteSession(token)) return { stale: true };
        return { stale: false, value };
      } catch (error) {
        if (!isCurrentRemoteSession(token)) return { stale: true };
        throw error;
      }
    };
    const reloadRemoteSnapshot = async (
      token: RemoteSessionToken,
      reconciliationMessage: string,
      currentSessionFailureMessage: string,
    ): Promise<boolean> => {
      const refreshSequence = ++remoteRefreshSequenceRef.current;
      try {
        const loaded = await guardedRemoteCall(token, () => loadRemoteData());
        if (
          loaded.stale ||
          refreshSequence !== remoteRefreshSequenceRef.current ||
          !isCurrentRemoteSession(token)
        ) return false;
        dataRef.current = loaded.value;
        setData(loaded.value);
        return true;
      } catch (error) {
        reconcileRemoteFailure(reconciliationMessage, error, token);
        if (!isCurrentRemoteSession(token)) return false;
        throw new Error(currentSessionFailureMessage);
      }
    };
    const reconcileRemoteFailure = (
      message: string,
      error: unknown,
      token: RemoteSessionToken,
    ) => {
      if (!isCurrentRemoteSession(token)) return;
      console.error(message, error);
      showFeedback(
        `${message} O COSMOS recarregou os dados confirmados pelo servidor.`,
      );
      const refreshSequence = ++remoteRefreshSequenceRef.current;
      void guardedRemoteCall(token, () => loadRemoteData())
        .then((loaded) => {
          if (
            loaded.stale ||
            !isCurrentRemoteSession(token) ||
            refreshSequence !== remoteRefreshSequenceRef.current
          ) return;
          dataRef.current = loaded.value;
          setData(loaded.value);
        })
        .catch((reloadError) => {
          if (!isCurrentRemoteSession(token) || refreshSequence !== remoteRefreshSequenceRef.current) return;
          console.error("Falha ao reconciliar dados com o Supabase", reloadError);
        });
    };
    const syncRemote = (
      operation: (token: RemoteSessionToken) => Promise<unknown>,
      message: string,
    ) => {
      const token = currentRemoteSession();
      void operation(token).catch((error) => reconcileRemoteFailure(message, error, token));
    };
    const remoteMutation = <T,>(
      token: RemoteSessionToken,
      operation: () => Promise<T>,
    ) => guardedRemoteCall(token, operation);
    const assertCanonicalLeadCreationAvailable = (current: AppData) => {
      assertIdentityWritable(current, isDemoMode);
      if (!canonicalLeadCreationEnabled(current))
        throw new Error("A criação canônica de Lead ainda não está disponível neste ambiente.");
    };
    const assertIdentityCandidateCommandAvailable = (current: AppData) => {
      assertIdentityWritable(current, isDemoMode);
      if (!isDemoMode && !current.canonicalLeadCreationCanaryReady)
        throw new Error("Os comandos candidatos de identidade ainda não estão disponíveis neste ambiente.");
    };
    const taskContext = (command: string, options?: TaskCommandOptions): CommandContext => {
      if (options?.context) return options.context;
      const ownerId = dataRef.current.demoOwnerId ?? "remote-owner";
      return createCommandContext({
        ownerId,
        actorType: isDemoMode ? "human" : "human",
        actorId: isDemoMode ? ownerId : "authenticated-user",
        origin: "ui",
        idempotencyKey: options?.idempotencyKey ?? `${command}:${crypto.randomUUID()}`,
        correlationId: options?.correlationId,
        causationId: options?.causationId,
        expectedVersion: options?.expectedVersion,
      });
    };
    const displayTaskFailure = (result: CommandResult<TaskCommandValue>) => {
      if (!result.ok && !isStaleRemoteSessionResult(result)) showFeedback(result.error.message);
      return result;
    };
    const staleTaskResult = (context: CommandContext): CommandResult<TaskCommandValue> =>
      commandError(
        context,
        "unauthenticated",
        "A sessão mudou; o resultado desta tarefa não foi aplicado nesta tela.",
        { severity: "info", details: { staleSession: true } },
      );
    const applyRemoteTaskResult = (result: CommandSuccess<TaskCommandValue>) => {
      setData((current) => ({
        ...current,
        tasks: current.tasks.some((task) => task.id === result.value.task.id)
          ? current.tasks.map((task) => task.id === result.value.task.id ? result.value.task : task)
          : [result.value.task, ...current.tasks],
        // Tarefa interna não produz evento de jornada: não há lead onde ele
        // apareceria (§7.4).
        events:
          !result.value.event || current.events.some((item) => item.id === result.value.event?.id)
            ? current.events
            : [...current.events, result.value.event],
      }));
    };
    const runDemoTransition = (
      transition: "completed" | "reopened",
      taskId: string,
      context: CommandContext,
    ) => {
      const adapter = taskAdapterRef.current;
      if (!adapter) return Promise.resolve(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      adapter.syncExternal(taskStateFromData(dataRef.current));
      const result = transition === "completed"
        ? adapter.complete(taskId, context)
        : adapter.reopen(taskId, context);
      return result.then(displayTaskFailure);
    };
    const runRemoteTransition = async (
      transition: "completed" | "reopened",
      taskId: string,
      context: CommandContext,
    ): Promise<CommandResult<TaskCommandValue>> => {
      const current = dataRef.current.tasks.find((task) => task.id === taskId);
      if (!current) return commandError(context, "not_found", "Tarefa não encontrada.", { severity: "warning" });
      const sessionToken = currentRemoteSession();
      try {
        const remoteCall = await guardedRemoteCall(sessionToken, () =>
          transition === "completed"
            ? remote.completeTask(taskId, context)
            : remote.reopenTask(taskId, context),
        );
        if (remoteCall.stale || !isCurrentRemoteSession(sessionToken)) return staleTaskResult(context);
        const remoteResult = remoteCall.value;
        if (!remoteResult.ok) return displayTaskFailure(remoteResult);
        applyRemoteTaskResult(remoteResult);
        return remoteResult;
      } catch (error) {
        if (!isCurrentRemoteSession(sessionToken)) return staleTaskResult(context);
        console.error("Falha ao persistir transição da tarefa", error);
        const result = commandError(context, "persistence_error", "Não foi possível atualizar a tarefa. Tente novamente.", {
          retryable: true,
          sanitizedCause: error instanceof Error ? error.message : "Falha remota não identificada",
        });
        return displayTaskFailure(result);
      }
    };
    const runTaskTransition = (
      transition: "completed" | "reopened",
      taskId: string,
      options?: TaskCommandOptions,
    ) => {
      const current = dataRef.current.tasks.find((task) => task.id === taskId);
      const context = taskContext(`tasks.${transition}`, {
        ...options,
        expectedVersion: options?.expectedVersion ?? current?.version ?? 1,
      });
      return isDemoMode
        ? runDemoTransition(transition, taskId, context)
        : runRemoteTransition(transition, taskId, context);
    };
    const runCommunicationCommand = async <T,>(
      command: string,
      run: (adapter: CommunicationDemoAdapter, context: CommandContext) => Promise<CommandResult<T>>,
      options?: CommandOptions,
    ): Promise<CommandResult<T>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<T>) => {
        // O painel trata esta incompatibilidade esperada iniciando uma nova
        // cadeia auditável. Não mostre um toast de falha antes da recuperação.
        if (!result.ok) {
          const recoverableContextChange =
            result.error.code === "invalid_state" &&
            result.error.message === AI_REGENERATION_CONTEXT_MISMATCH_MESSAGE;
          if (!recoverableContextChange) showFeedback(result.error.message);
        }
        return result;
      };
      // Fora do modo demo nao existe backend de e-mail, conexao, capability
      // positiva nem SMTP/IMAP: o comando recusa de forma tipada em vez de
      // simular persistencia.
      if (!isDemoMode || !dataRef.current.emailInboxAvailable)
        return report(commandError(
          context,
          "forbidden",
          "A Inbox depende de um backend de e-mail que ainda nao existe neste ambiente.",
          { severity: "warning" },
        ));
      const adapter = communicationAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponivel."));
      adapter.syncExternal(communicationStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runTemplateCommand = async (
      command: string,
      run: (
        adapter: CommunicationDemoAdapter,
        context: CommandContext,
      ) => Promise<CommandResult<TemplateValue>>,
      options?: CommandOptions,
    ): Promise<CommandResult<TemplateValue>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<TemplateValue>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      // Modelos são cadastro, não a Inbox: por isso não dependem da capability
      // de e-mail. Dependem de persistência, que fora do demo ainda não existe.
      if (!isDemoMode)
        return report(
          commandError(
            context,
            "forbidden",
            "Os modelos de mensagem ainda não têm persistência neste ambiente.",
            { severity: "warning" },
          ),
        );
      const adapter = communicationAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      adapter.syncExternal(communicationStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runAiCommand = async <T,>(
      command: string,
      run: (adapter: AiDemoAdapter, context: CommandContext) => Promise<CommandResult<T>>,
      options?: CommandOptions,
    ): Promise<CommandResult<T>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<T>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      // Fora do modo demo nao existe adapter, chave nem capability positiva de
      // IA: o comando recusa de forma tipada em vez de simular uma resposta.
      if (!isDemoMode || !dataRef.current.aiAssistanceAvailable)
        return report(commandError(
          context,
          "forbidden",
          "A assistencia de IA depende de um backend que ainda nao existe neste ambiente.",
          { severity: "warning" },
        ));
      const adapter = aiAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponivel."));
      adapter.syncExternal(aiStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runFlowCommand = async <T,>(
      command: string,
      run: (adapter: FlowDemoAdapter, context: CommandContext) => Promise<CommandResult<T>>,
      options?: CommandOptions,
      requiredCapability: "definition" | "publication" = "definition",
    ): Promise<CommandResult<T>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<T>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      // O adapter local possui a semantica de dominio da publicacao, mas a Store
      // somente a libera quando a capability correspondente vier positiva. Isso
      // impede estado salvo no navegador de simular uma fronteira remota ausente.
      const available =
        isDemoMode &&
        dataRef.current.flowDefinitionsAvailable &&
        (requiredCapability === "definition" || dataRef.current.flowPublicationAvailable);
      if (!available)
        return report(commandError(
          context,
          "forbidden",
          requiredCapability === "publication"
            ? "A publicação de flows ainda não está disponível neste ambiente."
            : "A definicao de flows depende de um schema que ainda nao existe neste ambiente.",
          { severity: "warning" },
        ));
      const adapter = flowAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponivel."));
      adapter.syncExternal(flowStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runFinanceCommand = async (
      command: string,
      run: (
        adapter: FinanceDemoAdapter,
        context: CommandContext,
      ) => Promise<CommandResult<FinanceCommandValue>>,
      options?: CommandOptions,
    ): Promise<CommandResult<FinanceCommandValue>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<FinanceCommandValue>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      // A capability `finance` nasce desligada no contrato do banco e não há RPC
      // neste ambiente. Falhar fechado aqui é o que impede a interface de
      // parecer que gravou um contrato que o banco nunca recebeu (AGENTS §9).
      if (!isDemoMode || !dataRef.current.financeAvailable)
        return report(
          commandError(
            context,
            "forbidden",
            "O módulo financeiro ainda não está disponível neste ambiente.",
            { severity: "warning" },
          ),
        );
      const adapter = financeAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      adapter.syncExternal(financeStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runClassificationCommand = async (
      command: string,
      run: (
        adapter: ClassificationDemoAdapter,
        context: CommandContext,
      ) => Promise<CommandResult<ClassificationCommandValue>>,
      options?: CommandOptions,
    ): Promise<CommandResult<ClassificationCommandValue>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<ClassificationCommandValue>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      // As tabelas existem no banco desde a migration inicial, mas a escrita
      // ainda não tem RPC com recibo e auditoria neste ambiente. Falhar fechado
      // evita a tela dizer "salvo" sobre uma gravação que não aconteceu.
      if (!isDemoMode)
        return report(
          commandError(
            context,
            "forbidden",
            "As classificações ainda não têm gravação remota neste ambiente.",
            { severity: "warning" },
          ),
        );
      const adapter = classificationAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      adapter.syncExternal(classificationStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    /**
     * Feed corrente, já com as categorias silenciadas pelo usuário aplicadas.
     * É a única montagem do feed no app: sino, central e comandos leem daqui,
     * para que "marcar todas" nunca alcance um aviso que a tela não mostrou.
     */
    const noticeFeed = (options?: { includeResolved?: boolean }): Notice[] => {
      const current = dataRef.current;
      const preferences = readPreference(current.userPreferences ?? [], "notifications");
      const feedInput: FeedInput = {
        today: currentCalendarDate(),
        leads: current.leads,
        memberships: current.memberships,
        tasks: current.tasks.filter((task) => !task.deletedAt),
        invoices: current.invoices,
        receipts: current.receipts,
        revenueGoals: current.revenueGoals,
        threads: current.communicationThreads,
        incidents: current.automationIncidents,
        events: current.events,
        attachments: current.attachments,
        states: current.notificationStates ?? [],
        mutedCategories: mutedCategories(preferences.value),
        includeResolved: options?.includeResolved,
      };
      return buildNoticeFeed(feedInput);
    };
    const runNotificationCommand = async (
      command: string,
      run: (
        adapter: NotificationDemoAdapter,
        context: CommandContext,
      ) => Promise<CommandResult<NotificationCommandValue>>,
      options?: CommandOptions,
    ): Promise<CommandResult<NotificationCommandValue>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<NotificationCommandValue>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      // `notification_states` ainda não existe no banco deste ambiente. Falhar
      // fechado evita a central dizer "lido" sobre uma marca que nada gravou.
      if (!isDemoMode)
        return report(
          commandError(
            context,
            "forbidden",
            "A central de notificações ainda não tem gravação remota neste ambiente.",
            { severity: "warning" },
          ),
        );
      const adapter = notificationAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      const feed = noticeFeed({ includeResolved: true });
      adapter.syncExternal({
        ...notificationStateFromData(dataRef.current),
        availableKeys: feed.map((notice) => notice.key),
        actionableKeys: feed.filter((notice) => notice.actionable).map((notice) => notice.key),
      });
      return report(await run(adapter, context));
    };
    const runPreferenceCommand = async (
      command: string,
      run: (
        adapter: PreferenceDemoAdapter,
        context: CommandContext,
      ) => Promise<CommandResult<PreferenceCommandValue>>,
      options?: CommandOptions,
    ): Promise<CommandResult<PreferenceCommandValue>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<PreferenceCommandValue>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      if (!isDemoMode)
        return report(
          commandError(
            context,
            "forbidden",
            "As preferências ainda não têm gravação remota neste ambiente.",
            { severity: "warning" },
          ),
        );
      const adapter = preferenceAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      adapter.syncExternal(preferenceStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runMeetingCommand = async (
      command: string,
      run: (
        adapter: MeetingDemoAdapter,
        context: CommandContext,
      ) => Promise<CommandResult<MeetingCommandValue>>,
      options?: CommandOptions,
    ): Promise<CommandResult<MeetingCommandValue>> => {
      const context = taskContext(command, options);
      const report = (result: CommandResult<MeetingCommandValue>) => {
        if (!result.ok) showFeedback(result.error.message);
        return result;
      };
      if (!isDemoMode)
        return report(
          commandError(
            context,
            "forbidden",
            "A agenda de reuniões ainda não tem gravação remota neste ambiente.",
            { severity: "warning" },
          ),
        );
      const adapter = meetingAdapterRef.current;
      if (!adapter)
        return report(commandError(context, "persistence_error", "Adaptador demo indisponível."));
      adapter.syncExternal(meetingStateFromData(dataRef.current));
      return report(await run(adapter, context));
    };
    const runCreateTask = async (
      taskInput: TaskDraftInput,
      options?: TaskCommandOptions,
    ): Promise<CommandResult<TaskCommandValue>> => {
      const context = taskContext("tasks.create", options);
      if (isDemoMode) {
        const adapter = taskAdapterRef.current;
        if (!adapter) return displayTaskFailure(commandError(context, "persistence_error", "Adaptador demo indisponível."));
        adapter.syncExternal(taskStateFromData(dataRef.current));
        return displayTaskFailure(await adapter.create(taskInput, context));
      }
      const sessionToken = currentRemoteSession();
      try {
        const remoteCall = await guardedRemoteCall(sessionToken, () => remote.createTask(taskInput, context));
        if (remoteCall.stale || !isCurrentRemoteSession(sessionToken)) return staleTaskResult(context);
        const result = remoteCall.value;
        if (!result.ok) return displayTaskFailure(result);
        applyRemoteTaskResult(result);
        return result;
      } catch (error) {
        // guardedRemoteCall consumes failures from an obsolete account. Keep
        // this check as a second line of defense for future remote adapters.
        if (!isCurrentRemoteSession(sessionToken)) return staleTaskResult(context);
        console.error("Falha ao persistir criação da tarefa", error);
        return displayTaskFailure(commandError(context, "persistence_error", "Não foi possível criar a tarefa. Tente novamente.", {
          retryable: true,
          sanitizedCause: error instanceof Error ? error.message : "Falha remota não identificada",
        }));
      }
    };
    return {
      ...data,
      // Tarefa excluída é marca de histórico, não item de lista: nenhuma
      // superfície a enxerga (Task.deletedAt).
      tasks: data.tasks.filter((task) => !task.deletedAt),
      aiProviderMode: LOCAL_GROQ_AI_AVAILABLE ? "local_groq" : "simulated",
      saveEmailDraft(input, options) {
        return runCommunicationCommand(
          "communication.draft.save",
          (adapter, context) => adapter.saveDraft(input, context),
          options,
        );
      },
      queueEmailSend(draftId, options) {
        const draft = dataRef.current.communicationMessages?.find((item) => item.id === draftId);
        return runCommunicationCommand(
          "communication.send.queue",
          (adapter, context) => adapter.queueSend({ draftId, sentInRun: options?.sentInRun ?? 0 }, context),
          { ...options, expectedVersion: options?.expectedVersion ?? draft?.version },
        );
      },
      linkEmailThread(input, options) {
        const thread = dataRef.current.communicationThreads?.find((item) => item.id === input.threadId);
        return runCommunicationCommand(
          "communication.thread.link",
          (adapter, context) => adapter.linkThread(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? thread?.version },
        );
      },
      transitionEmailThread(input, options) {
        const thread = dataRef.current.communicationThreads?.find((item) => item.id === input.threadId);
        return runCommunicationCommand(
          "communication.thread.transition",
          (adapter, context) => adapter.threadTransition(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? thread?.version },
        );
      },
      sendChannelMessage(input, options) {
        const thread = dataRef.current.communicationThreads?.find((item) => item.id === input.threadId);
        return runCommunicationCommand(
          "communication.channel.send",
          (adapter, context) => adapter.sendChannelMessage(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? thread?.version },
        );
      },
      markChannelMessage(input, options) {
        const message = dataRef.current.communicationMessages?.find((item) => item.id === input.messageId);
        return runCommunicationCommand(
          "communication.message.mark",
          (adapter, context) => adapter.markChannelMessage(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? message?.version },
        );
      },
      deleteChannelMessage(input, options) {
        const message = dataRef.current.communicationMessages?.find((item) => item.id === input.messageId);
        return runCommunicationCommand(
          "communication.message.delete",
          (adapter, context) => adapter.deleteChannelMessage(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? message?.version },
        );
      },
      createMessageTemplate(input, options) {
        return runTemplateCommand(
          "communication.template.create",
          (adapter, context) => adapter.createTemplate(input, context),
          options,
        );
      },
      saveMessageTemplateDraft(input, options) {
        return runTemplateCommand(
          "communication.template.save_draft",
          (adapter, context) => adapter.saveTemplateDraft(input, context),
          options,
        );
      },
      publishMessageTemplateVersion(input, options) {
        return runTemplateCommand(
          "communication.template.publish",
          (adapter, context) => adapter.publishTemplateVersion(input, context),
          options,
        );
      },
      archiveMessageTemplate(input, options) {
        return runTemplateCommand(
          "communication.template.archive",
          (adapter, context) => adapter.archiveTemplate(input, context),
          options,
        );
      },
      requestAiSuggestion(input, options) {
        return runAiCommand(
          "ai.suggestion.request",
          (adapter, context) => adapter.requestSuggestion(input, context),
          options,
        );
      },
      recordAiFeedback(input, options) {
        return runAiCommand(
          "ai.feedback.record",
          (adapter, context) => adapter.recordFeedback(input, context),
          options,
        );
      },
      createFlowDraft(input, options) {
        return runFlowCommand(
          "flows.draft.create",
          (adapter, context) => adapter.createDraft(input, context),
          options,
        );
      },
      saveFlowDraft(input, options) {
        const draft = dataRef.current.flowDrafts?.find((item) => item.id === input.draftId);
        return runFlowCommand(
          "flows.draft.save",
          (adapter, context) => adapter.saveDraft(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? draft?.revision },
        );
      },
      publishFlowDraft(input, options) {
        const draft = dataRef.current.flowDrafts?.find((item) => item.id === input.draftId);
        return runFlowCommand(
          "flows.publish",
          (adapter, context) => adapter.publishDraft(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? draft?.revision },
          "publication",
        );
      },
      setFlowEnrollmentPaused(flowId, paused) {
        if (!isDemoMode || !dataRef.current.flowPublicationAvailable) return false;
        const active = dataRef.current.flowActiveVersions?.find((item) => item.flowId === flowId);
        if (!active) return false;
        const next = paused ? { ...active, pausedAt: new Date().toISOString() } : { ...active, pausedAt: undefined };
        const apply = (current: AppData): AppData => ({
          ...current,
          flowActiveVersions: (current.flowActiveVersions ?? []).map((item) =>
            item.flowId === flowId ? next : item,
          ),
        });
        dataRef.current = apply(dataRef.current);
        setData(apply);
        return true;
      },
      duplicateFlowDraft(input, options) {
        return runFlowCommand(
          "flows.draft.duplicate",
          (adapter, context) => adapter.duplicateDraft(input, context),
          options,
        );
      },
      archiveFlowDraft(input, options) {
        const draft = dataRef.current.flowDrafts?.find((item) => item.id === input.draftId);
        return runFlowCommand(
          "flows.draft.archive",
          (adapter, context) => adapter.archiveDraft(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? draft?.revision },
        );
      },
      restoreFlowDraft(input, options) {
        const draft = dataRef.current.flowDrafts?.find((item) => item.id === input.draftId);
        return runFlowCommand(
          "flows.draft.restore",
          (adapter, context) => adapter.restoreDraft(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? draft?.revision },
        );
      },
      createClassification(input, options) {
        return runClassificationCommand(
          "classifications.create",
          (adapter, context) => adapter.create(input, context),
          options,
        );
      },
      renameClassification(input, options) {
        return runClassificationCommand(
          "classifications.rename",
          (adapter, context) => adapter.rename(input, context),
          options,
        );
      },
      archiveClassification(input, options) {
        return runClassificationCommand(
          "classifications.archive",
          (adapter, context) => adapter.archive(input, context),
          options,
        );
      },
      removeClassification(input, options) {
        return runClassificationCommand(
          "classifications.remove",
          (adapter, context) => adapter.remove(input, context),
          options,
        );
      },
      createClassificationOption(input, options) {
        return runClassificationCommand(
          "classifications.option.create",
          (adapter, context) => adapter.createOption(input, context),
          options,
        );
      },
      renameClassificationOption(input, options) {
        return runClassificationCommand(
          "classifications.option.rename",
          (adapter, context) => adapter.renameOption(input, context),
          options,
        );
      },
      archiveClassificationOption(input, options) {
        return runClassificationCommand(
          "classifications.option.archive",
          (adapter, context) => adapter.archiveOption(input, context),
          options,
        );
      },
      removeClassificationOption(input, options) {
        return runClassificationCommand(
          "classifications.option.remove",
          (adapter, context) => adapter.removeOption(input, context),
          options,
        );
      },
      reorderClassifications(input, options) {
        return runClassificationCommand(
          "classifications.reorder",
          (adapter, context) => adapter.reorder(input, context),
          options,
        );
      },
      setLeadClassification(input, options) {
        return runClassificationCommand(
          "classifications.set_value",
          (adapter, context) => adapter.setLeadValue(input, context),
          options,
        );
      },
      noticeFeed,
      markNoticeRead(input, options) {
        return runNotificationCommand(
          "notifications.mark_read",
          (adapter, context) => adapter.markRead(input, context),
          options,
        );
      },
      resolveNotice(input, options) {
        return runNotificationCommand(
          "notifications.resolve",
          (adapter, context) => adapter.resolve(input, context),
          options,
        );
      },
      markAllNoticesRead(input, options) {
        return runNotificationCommand(
          "notifications.mark_all_read",
          (adapter, context) => adapter.markAllRead(input, context),
          options,
        );
      },
      setPreference(input, options) {
        return runPreferenceCommand(
          "preferences.set",
          (adapter, context) => adapter.set(input, context),
          options,
        );
      },
      scheduleMeeting(input, options) {
        return runMeetingCommand(
          "meetings.schedule",
          (adapter, context) => adapter.schedule(input, context),
          options,
        );
      },
      rescheduleMeeting(input, options) {
        return runMeetingCommand(
          "meetings.reschedule",
          (adapter, context) => adapter.reschedule(input, context),
          options,
        );
      },
      updateMeeting(input, options) {
        return runMeetingCommand(
          "meetings.update",
          (adapter, context) => adapter.update(input, context),
          options,
        );
      },
      holdMeeting(input, options) {
        return runMeetingCommand(
          "meetings.hold",
          (adapter, context) => adapter.hold(input, context),
          options,
        );
      },
      cancelMeeting(input, options) {
        return runMeetingCommand(
          "meetings.cancel",
          (adapter, context) => adapter.cancel(input, context),
          options,
        );
      },
      createContract(input, options) {
        return runFinanceCommand(
          "finance.contract.create",
          (adapter, context) => adapter.createContract(input, context),
          options,
        );
      },
      generateContractInvoices(input, options) {
        return runFinanceCommand(
          "finance.contract.generate_invoices",
          (adapter, context) => adapter.generateInvoices(input, context),
          options,
        );
      },
      addExtraInvoice(input, options) {
        return runFinanceCommand(
          "finance.invoice.add_extra",
          (adapter, context) => adapter.addExtraInvoice(input, context),
          options,
        );
      },
      registerReceipt(input, options) {
        return runFinanceCommand(
          "finance.receipt.register",
          (adapter, context) => adapter.registerReceipt(input, context),
          options,
        );
      },
      cancelContract(input, options) {
        const contract = dataRef.current.contracts?.find((item) => item.id === input.contractId);
        return runFinanceCommand(
          "finance.contract.cancel",
          (adapter, context) => adapter.cancelContract(input, context),
          { ...options, expectedVersion: options?.expectedVersion ?? contract?.version },
        );
      },
      setRevenueGoal(input, options) {
        return runFinanceCommand(
          "finance.goal.set",
          (adapter, context) => adapter.setRevenueGoal(input, context),
          options,
        );
      },
      validateFlowDraft(draftId) {
        return validateFlowDraftById(flowStateFromData(dataRef.current), draftId);
      },
      compileFlowDraft(draftId) {
        return compileFlowDraftById(flowStateFromData(dataRef.current), draftId);
      },
      importEmailMessage(input, options) {
        return runCommunicationCommand(
          "communication.message.import",
          (adapter, context) => adapter.importMessage(input, context),
          options,
        );
      },
      moveMembership(membershipId, stageId) {
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.moveMembership(membershipId, stageId)),
            "Não foi possível mover o lead.",
          );
        setData((d) => {
          const membership = d.memberships.find((m) => m.id === membershipId);
          const stage = d.stages.find((s) => s.id === stageId);
          if (!membership || !stage || membership.stageId === stageId) return d;
          const old = d.stages.find((s) => s.id === membership.stageId);
          const now = new Date().toISOString();
          const event: ActivityEvent = {
            id: crypto.randomUUID(),
            leadId: membership.leadId,
            membershipId,
            type: "stage_changed",
            title: stage.name,
            description: `Movido de ${old?.name ?? "outra etapa"}`,
            occurredAt: now,
            stageId,
            fromStageId: membership.stageId,
            kind: "stage",
            durationLabel: "agora",
          };
          return {
            ...d,
            memberships: d.memberships.map((m) =>
              m.id === membershipId
                ? { ...m, stageId, enteredStageAt: now, status: "active" }
                : m,
            ),
            events: [...d.events, event],
          };
        });
      },
      reorderMembership(membershipId, stageId, targetIds, sourceIds) {
        const current = data.memberships.find((m) => m.id === membershipId);
        if (!current) return;
        const movedStage = current.stageId !== stageId;
        if (!isDemoMode)
          syncRemote(
            async (token) => {
              if (movedStage) {
                const moved = await guardedRemoteCall(token, () => remote.moveMembership(membershipId, stageId));
                if (moved.stale) return;
              }
              const ordered = await guardedRemoteCall(token, () => remote.saveMembershipOrder([...targetIds, ...sourceIds]));
              if (ordered.stale) return;
            },
            "Não foi possível salvar a ordem do Pipeline.",
          );
        setData((d) => {
          const oldStage = d.stages.find((s) => s.id === current.stageId);
          const newStage = d.stages.find((s) => s.id === stageId);
          const now = new Date().toISOString();
          const targetOrder = new Map(
            targetIds.map((id, index) => [id, index + 1]),
          );
          const sourceOrder = new Map(
            sourceIds.map((id, index) => [id, index + 1]),
          );
          const memberships = d.memberships.map((m) => {
            const next =
              m.id === membershipId && movedStage
                ? {
                    ...m,
                    stageId,
                    enteredStageAt: now,
                    status: "active" as const,
                  }
                : m;
            const order = targetOrder.get(next.id) ?? sourceOrder.get(next.id);
            return order === undefined ? next : { ...next, sortOrder: order };
          });
          if (!movedStage) return { ...d, memberships };
          const event: ActivityEvent = {
            id: crypto.randomUUID(),
            leadId: current.leadId,
            membershipId,
            type: "stage_changed",
            title: newStage?.name ?? "Etapa alterada",
            description: `Movido de ${oldStage?.name ?? "outra etapa"}`,
            occurredAt: now,
            stageId,
            fromStageId: current.stageId,
            kind: "stage",
            durationLabel: "agora",
          };
          return {
            ...d,
            memberships,
            events: [...d.events, event],
          };
        });
      },
      setMembershipOutcome(membershipId, status, lossReasonId) {
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(
              token,
              () => status === "active"
                ? remote.reactivate(membershipId)
                : remote.closeMembership(membershipId, status, lossReasonId),
            ),
            "Não foi possível atualizar o desfecho da participação.",
          );
        setData((d) => {
          const m = d.memberships.find((x) => x.id === membershipId);
          if (!m) return d;
          const now = new Date().toISOString();
          const title =
            status === "won"
              ? "Lead ganho"
              : status === "lost"
                ? "Lead perdido"
                : "Lead reativado";
          return {
            ...d,
            memberships: d.memberships.map((x) =>
              x.id === membershipId
                ? {
                    ...x,
                    status,
                    lossReasonId: status === "lost" ? lossReasonId : undefined,
                    closedAt: status === "active" ? undefined : now,
                  }
                : x,
            ),
            events: [
              ...d.events,
              {
                id: crypto.randomUUID(),
                leadId: m.leadId,
                membershipId,
                type:
                  status === "active" ? "lead_reactivated" : `lead_${status}`,
                title,
                occurredAt: now,
                stageId: m.stageId,
                lossReasonId: status === "lost" ? lossReasonId : undefined,
                previousStatus: m.status,
                previousLossReasonId: m.lossReasonId,
                kind: "outcome",
              },
            ],
          };
        });
      },
      deleteJourneyEvent(eventId) {
        const target = data.events.find((event) => event.id === eventId);
        if (!target) return;
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.deleteJourneyEvent(eventId)),
            "Não foi possível desfazer o registro da jornada.",
          );
        setData((d) => {
          const event = d.events.find((item) => item.id === eventId);
          if (!event) return d;
          const related = event.membershipId
            ? d.events
                .filter(
                  (item) =>
                    item.membershipId === event.membershipId &&
                    item.kind === "stage" &&
                    item.type === "stage_changed" &&
                    item.id !== event.id,
                )
                .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
            : [];
          const latestStageEvent = related.at(-1);
          const outcomeTypes = ["lead_won", "lead_lost", "lead_reactivated"];
          const relatedOutcomes = event.membershipId
            ? d.events
                .filter(
                  (item) =>
                    item.membershipId === event.membershipId &&
                    item.kind === "outcome" &&
                    outcomeTypes.includes(item.type) &&
                    item.id !== event.id,
                )
                .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
            : [];
          const latestOutcome = relatedOutcomes.at(-1);
          const membership = event.membershipId
            ? d.memberships.find((item) => item.id === event.membershipId)
            : undefined;
          const isLatestStage =
            event.kind === "stage" &&
            event.type === "stage_changed" &&
            (!latestStageEvent ||
              event.occurredAt >= latestStageEvent.occurredAt);
          const isLatestOutcome =
            event.kind === "outcome" &&
            outcomeTypes.includes(event.type) &&
            (!latestOutcome || event.occurredAt >= latestOutcome.occurredAt);
          const replacementStageId = isLatestStage
            ? event.fromStageId || latestStageEvent?.stageId
            : undefined;
          const replacementStatus = isLatestOutcome
            ? event.previousStatus ||
              (latestOutcome?.type === "lead_lost"
                ? "lost"
                : latestOutcome?.type === "lead_won"
                  ? "won"
                  : "active")
            : undefined;
          const replacementLossReasonId = isLatestOutcome
            ? event.previousLossReasonId || latestOutcome?.lossReasonId
            : undefined;
          return {
            ...d,
            events: d.events.filter((item) => item.id !== eventId),
            memberships:
              membership && (replacementStageId || replacementStatus)
                ? d.memberships.map((item) =>
                    item.id === membership.id
                      ? {
                          ...item,
                          ...(replacementStageId
                            ? {
                                stageId: replacementStageId,
                                enteredStageAt:
                                  latestStageEvent?.occurredAt ??
                                  item.enteredStageAt,
                              }
                            : {}),
                          ...(replacementStatus
                            ? {
                                status: replacementStatus,
                                lossReasonId:
                                  replacementStatus === "lost"
                                    ? replacementLossReasonId
                                    : undefined,
                                closedAt:
                                  replacementStatus === "active"
                                    ? undefined
                                    : (latestOutcome?.occurredAt ??
                                      item.closedAt),
                              }
                            : {}),
                        }
                      : item,
                  )
                : d.memberships,
          };
        });
      },
      toggleTask(taskId, options) {
        const current = dataRef.current.tasks.find((task) => task.id === taskId);
        if (!current) {
          const context = taskContext("tasks.toggle", options);
          return Promise.resolve(commandError(context, "not_found", "Tarefa não encontrada.", { severity: "warning" }));
        }
        return runTaskTransition(current.status === "pending" ? "completed" : "reopened", taskId, options);
        /*
        if (!current) return;
        if (!isDemoMode)
          syncRemote(
            current.status === "pending"
              ? remote.completeTask(taskId)
              : remote.reopenTask(taskId),
            "Não foi possível atualizar a tarefa.",
          );
        setData((d) => ({
          ...d,
          tasks: d.tasks.map((t) =>
            t.id === taskId
              ? ({
                  ...t,
                  status: t.status === "done" ? "pending" : "done",
                  completedAt:
                    t.status === "done" ? undefined : new Date().toISOString(),
                } as Task)
              : t,
          ),
        }));
      */
      },
      completeTask(taskId, options) {
        return runTaskTransition("completed", taskId, options);
      },
      reopenTask(taskId, options) {
        return runTaskTransition("reopened", taskId, options);
      },
      setTaskProgress(taskId, progress, options) {
        const context = taskContext("tasks.progress", options);
        // O andamento é coluna nova, ainda sem RPC no banco. Fora do demo a
        // operação falha fechada em vez de mudar só a tela (AGENTS §9, §25.1).
        if (!isDemoMode)
          return Promise.resolve(
            displayTaskFailure(
              commandError(
                context,
                "forbidden",
                "O andamento das tarefas ainda não está disponível neste ambiente.",
                { severity: "warning" },
              ),
            ),
          );
        const adapter = taskAdapterRef.current;
        if (!adapter)
          return Promise.resolve(
            commandError(context, "persistence_error", "Adaptador demo indisponível."),
          );
        adapter.syncExternal(taskStateFromData(dataRef.current));
        return adapter.setProgress({ taskId, progress }, context).then(displayTaskFailure);
      },
      deleteTask(taskId, options) {
        const context = taskContext("tasks.delete", options);
        // Excluir é comando novo, ainda sem RPC no banco. Fora do demo falha
        // fechada em vez de sumir só da tela (§9, §25.1).
        if (!isDemoMode)
          return Promise.resolve(
            displayTaskFailure(
              commandError(
                context,
                "forbidden",
                "Excluir tarefa ainda não está disponível neste ambiente.",
                { severity: "warning" },
              ),
            ),
          );
        const adapter = taskAdapterRef.current;
        if (!adapter)
          return Promise.resolve(
            commandError(context, "persistence_error", "Adaptador demo indisponível."),
          );
        adapter.syncExternal(taskStateFromData(dataRef.current));
        return adapter.remove(taskId, context).then(displayTaskFailure);
      },
      updateTask(input, options) {
        const context = taskContext("tasks.update", options);
        // Editar é comando novo, ainda sem RPC no banco. Fora do demo falha
        // fechada em vez de mudar só a tela (§9, §25.1).
        if (!isDemoMode)
          return Promise.resolve(
            displayTaskFailure(
              commandError(
                context,
                "forbidden",
                "Editar tarefa ainda não está disponível neste ambiente.",
                { severity: "warning" },
              ),
            ),
          );
        const adapter = taskAdapterRef.current;
        if (!adapter)
          return Promise.resolve(
            commandError(context, "persistence_error", "Adaptador demo indisponível."),
          );
        adapter.syncExternal(taskStateFromData(dataRef.current));
        return adapter.update(input, context).then(displayTaskFailure);
      },
      addNote(leadId, text) {
        const event = {
          id: crypto.randomUUID(),
          leadId,
          type: "note_added",
          title: "Nota adicionada",
          description: text,
          occurredAt: new Date().toISOString(),
          kind: "event" as const,
        };
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.addEvent(event)),
            "Não foi possível salvar a nota.",
          );
        setData((d) => ({ ...d, events: [...d.events, event] }));
      },
      async previewRelatedLeadInFunnel(sourceLeadId, targetFunnelId) {
        const current = dataRef.current;
        assertCanonicalLeadCreationAvailable(current);
        if (isDemoMode) return previewRelatedLeadCommand(current, sourceLeadId, targetFunnelId);
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () =>
          remote.previewRelatedLeadInFunnel(sourceLeadId, targetFunnelId),
        );
        return result.stale ? undefined : result.value;
      },
      async createRelatedLeadInFunnel(input) {
        const current = dataRef.current;
        assertCanonicalLeadCreationAvailable(current);
        if (isDemoMode) {
          const adapter = identityAdapterRef.current;
          if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
          adapter.syncExternal(current);
          const result = await adapter.createRelatedLeadInFunnel(input);
          dataRef.current = result.nextData;
          setData(result.nextData);
          return result;
        }
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () =>
          remote.createRelatedLeadInFunnel(input),
        );
        if (result.stale) return undefined;
        const reloaded = await reloadRemoteSnapshot(
          sessionToken,
          "A continuidade foi criada, mas não foi possível confirmar o reload.",
          "A continuidade foi persistida, mas a atualização da lista falhou. Recarregue a página.",
        );
        return reloaded && isCurrentRemoteSession(sessionToken) ? result.value : undefined;
      },
      async previewReclassifyPartyChannel(channelId, targetContactId) {
        const current = dataRef.current;
        assertIdentityCandidateCommandAvailable(current);
        if (isDemoMode) {
          const adapter = identityAdapterRef.current;
          if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
          adapter.syncExternal(current);
          return adapter.previewReclassifyPartyChannel(channelId, targetContactId);
        }
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () =>
          remote.previewReclassifyPartyChannel(channelId, targetContactId),
        );
        return result.stale ? undefined : result.value;
      },
      async reclassifyPartyChannel(input) {
        const current = dataRef.current;
        assertIdentityCandidateCommandAvailable(current);
        if (isDemoMode) {
          const adapter = identityAdapterRef.current;
          if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
          adapter.syncExternal(current);
          const result = await adapter.reclassifyPartyChannel(input);
          dataRef.current = result.nextData;
          setData(result.nextData);
          return {
            channelId: result.channel.id,
            targetContactId: result.channel.contactId,
            idempotent: result.idempotent,
            commandId: result.audit.commandId,
          };
        }
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () =>
          remote.reclassifyPartyChannel(input),
        );
        if (result.stale) return undefined;
        const reloaded = await reloadRemoteSnapshot(
          sessionToken,
          "A reclassificação foi registrada, mas não foi possível confirmar o reload.",
          "A reclassificação foi persistida, mas a atualização dos canais falhou. Recarregue a página.",
        );
        return reloaded && isCurrentRemoteSession(sessionToken) ? result.value : undefined;
      },
      async reviewIdentityCandidate(input) {
        const current = dataRef.current;
        assertIdentityCandidateCommandAvailable(current);
        if (isDemoMode) {
          const adapter = identityAdapterRef.current;
          if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
          adapter.syncExternal(current);
          const result = await adapter.reviewIdentityCandidate(input);
          dataRef.current = result.nextData;
          setData(result.nextData);
          return {
            candidateId: result.candidateId,
            decision: result.decision,
            idempotent: result.idempotent,
            commandId: result.audit.commandId,
          };
        }
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () =>
          remote.reviewIdentityCandidate(input),
        );
        if (result.stale) return undefined;
        const reloaded = await reloadRemoteSnapshot(
          sessionToken,
          "A revisão foi registrada, mas não foi possível confirmar o reload.",
          "A revisão foi persistida, mas a atualização da fila falhou. Recarregue a página.",
        );
        return reloaded && isCurrentRemoteSession(sessionToken) ? result.value : undefined;
      },
      async previewMergeCompanies(sourceCompanyId, targetCompanyId) {
        const current = dataRef.current;
        assertIdentityCandidateCommandAvailable(current);
        if (isDemoMode) {
          const adapter = identityAdapterRef.current;
          if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
          adapter.syncExternal(current);
          return adapter.previewMergeCompanies(sourceCompanyId, targetCompanyId);
        }
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () =>
          remote.previewMergeCompanies(sourceCompanyId, targetCompanyId),
        );
        return result.stale ? undefined : result.value;
      },
      async mergeCompanies(input) {
        const current = dataRef.current;
        assertIdentityCandidateCommandAvailable(current);
        if (isDemoMode) {
          const adapter = identityAdapterRef.current;
          if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
          adapter.syncExternal(current);
          const result = await adapter.mergeCompanies(input);
          dataRef.current = result.nextData;
          setData(result.nextData);
          return {
            idempotent: result.idempotent,
            sourceCompanyId: result.sourceCompanyId,
            targetCompanyId: result.targetCompanyId,
            commandId: result.audit.commandId ?? result.audit.id,
            tombstone: result.tombstone,
            preservedEntityIds: result.preservedEntityIds,
            historyRewritten: result.historyRewritten,
          };
        }
        const sessionToken = currentRemoteSession();
        const result = await guardedRemoteCall(sessionToken, () => remote.mergeCompanies(input));
        if (result.stale) return undefined;
        const reloaded = await reloadRemoteSnapshot(
          sessionToken,
          "A mesclagem foi registrada, mas não foi possível confirmar o reload.",
          "A mesclagem foi persistida, mas a atualização das empresas falhou. Recarregue a página.",
        );
        return reloaded && isCurrentRemoteSession(sessionToken) ? result.value : undefined;
      },
      async addLead(lead, funnelId, stageId, initialNote, options) {
        const { fieldValues = {}, ...leadCore } = lead;
        const current = dataRef.current;
        if (canonicalLeadCreationEnabled(current)) {
          assertCanonicalLeadCreationAvailable(current);
          const canonicalInput = buildCreateOrReuseCompanyLeadInput(
            current,
            {
              ...leadCore,
              tagIds: lead.tagIds ?? [],
              triggerIds: lead.triggerIds ?? [],
              fieldValues,
            },
            funnelId,
            stageId,
            initialNote,
            options?.idempotencyKey ?? `lead.create:${crypto.randomUUID()}`,
          );
          if (isDemoMode) {
            const adapter = identityAdapterRef.current;
            if (!adapter) throw new Error("Adaptador de identidade demo indisponível.");
            adapter.syncExternal(current);
            const result = await adapter.createOrReuseCompanyLead(canonicalInput);
            dataRef.current = result.nextData;
            setData(result.nextData);
            return result.leadId;
          }
          const sessionToken = currentRemoteSession();
          const result = await guardedRemoteCall(sessionToken, () =>
            remote.createOrReuseCompanyLead(canonicalInput),
          );
          if (result.stale) return "";
          const reloaded = await reloadRemoteSnapshot(
            sessionToken,
            "O lead foi criado, mas não foi possível confirmar o reload.",
            "O lead foi persistido, mas a atualização da lista falhou. Recarregue a página.",
          );
          return reloaded && isCurrentRemoteSession(sessionToken) ? result.value.leadId : "";
        }
        const now = new Date().toISOString(),
          identity = prepareLeadIdentity(current, leadCore, isDemoMode, {
            now: () => now,
            uuid: () => crypto.randomUUID(),
          }),
          id = crypto.randomUUID(),
          created = {
            ...leadCore,
            ...identity.leadPatch,
            id,
            tagIds: lead.tagIds ?? [],
            triggerIds: lead.triggerIds ?? [],
            createdAt: now,
          };
        let membershipId = isDemoMode ? crypto.randomUUID() : "";
        const createdEvent = {
            id: crypto.randomUUID(),
            leadId: id,
            membershipId,
            type: "lead_created",
            title: "Lead criado",
            occurredAt: now,
            kind: "event" as const,
          },
          noteEvent = initialNote?.trim()
            ? {
                id: crypto.randomUUID(),
                leadId: id,
                type: "note_added",
                title: "Nota adicionada",
                description: initialNote.trim(),
                occurredAt: now,
                kind: "event" as const,
              }
            : undefined;
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (!isDemoMode) {
          const createdMembership = await guardedRemoteCall(
            sessionToken!,
            () => remote.addLead(created, funnelId, stageId),
          );
          if (createdMembership.stale) return "";
          membershipId = createdMembership.value;
        }
        if (!isDemoMode && Object.values(fieldValues).some((value) => value !== null && value !== "" && !(Array.isArray(value) && value.length === 0))) {
          const savedFields = await guardedRemoteCall(
            sessionToken!,
            () => remote.saveLeadFieldValues(id, fieldValues),
          );
          if (savedFields.stale) return "";
        }
        if (noteEvent && !isDemoMode) {
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.addEvent(noteEvent)),
            "O lead foi criado, mas não foi possível salvar o comentário inicial.",
          );
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return "";
        createdEvent.membershipId = membershipId;
        setData((d) => applyCreatedLeadIdentity({
          ...d,
          leads: [created, ...d.leads],
          memberships: [
            {
              id: membershipId,
              leadId: id,
              funnelId,
              stageId,
              status: "active",
              enteredStageAt: now,
            },
            ...d.memberships,
          ],
          events: [
            ...d.events,
            createdEvent,
            ...(noteEvent ? [noteEvent] : []),
          ],
          leadFieldValues: [
            ...d.leadFieldValues,
            ...Object.entries(fieldValues)
              .filter(([, value]) => value !== null && value !== "" && !(Array.isArray(value) && value.length === 0))
              .map(([fieldId, value]) => ({ leadId: id, fieldId, value })),
          ],
        }, id, identity, now));
        return id;
      },
      async addCompany(input) {
        assertIdentityWritable(data, isDemoMode);
        const company = buildCompany(input, data.companies, {
          now: () => new Date().toISOString(),
          uuid: () => crypto.randomUUID(),
        });
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.addCompany(company));
          if (result.stale) return "";
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return "";
        setData((d) => ({ ...d, companies: [company, ...d.companies] }));
        return company.id;
      },
      async updateCompany(companyId, patch) {
        assertIdentityWritable(data, isDemoMode);
        const current = data.companies.find((company) => company.id === companyId);
        if (!current) return false;
        const next = buildCompanyUpdate(current, patch, data.companies, new Date().toISOString());
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.updateCompany(companyId, next));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => applyCompanyUpdate(d, current, next));
        return true;
      },
      async addContact(input) {
        assertIdentityWritable(data, isDemoMode);
        assertActiveCompany(dataRef.current.companies, input.companyId);
        const contact = buildContact(input, {
          now: () => new Date().toISOString(),
          uuid: () => crypto.randomUUID(),
        });
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.addContact(contact));
          if (result.stale) return "";
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return "";
        setData((d) => ({ ...d, contacts: [contact, ...d.contacts] }));
        return contact.id;
      },
      async updateContact(contactId, patch) {
        assertIdentityWritable(data, isDemoMode);
        const current = data.contacts.find((contact) => contact.id === contactId);
        if (!current) return false;
        assertActiveCompany(dataRef.current.companies, current.companyId);
        const next = buildContactUpdate(current, patch, new Date().toISOString());
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.updateContact(contactId, next));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => applyContactUpdate(d, current, next));
        return true;
      },
      async setLeadContacts(leadId, contactIds, primaryContactId) {
        assertIdentityWritable(data, isDemoMode);
        validateLeadContacts(data, leadId, contactIds, primaryContactId);
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.setLeadContacts(leadId, contactIds, primaryContactId));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        const now = new Date().toISOString();
        setData((d) => applyLeadContacts(d, leadId, contactIds, primaryContactId, now));
        return true;
      },
      async setLeadPhoto(leadId, photo) {
        if (typeof photo === "string") {
          setData((d) => ({
            ...d,
            leads: d.leads.map((lead) =>
              lead.id === leadId ? { ...lead, photoUrl: photo } : lead,
              ),
          }));
          return true;
        }
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.uploadPhoto(leadId, photo));
            if (result.stale) return false;
            const photoUrl = result.value;
            if (!isCurrentRemoteSession(sessionToken)) return false;
            setData((d) => ({
              ...d,
              leads: d.leads.map((l) =>
                l.id === leadId ? { ...l, photoUrl } : l,
              ),
            }));
            return true;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao alterar foto do lead", error);
            showFeedback(
              "Não foi possível alterar a foto do lead. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        await new Promise<void>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            setData((d) => ({
              ...d,
              leads: d.leads.map((l) =>
                l.id === leadId ? { ...l, photoUrl: String(reader.result) } : l,
              ),
            }));
            resolve();
          };
          reader.onerror = () =>
            reject(reader.error ?? new Error("Não foi possível ler a imagem"));
          reader.readAsDataURL(photo);
        });
        return true;
      },
      async updateLead(leadId, patch) {
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.updateLead(leadId, patch));
            if (result.stale) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao salvar lead", error);
            showFeedback(
              "Não foi possível salvar as alterações do lead. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        setData((d) => ({
          ...d,
          leads: d.leads.map((l) => (l.id === leadId ? { ...l, ...patch } : l)),
        }));
        return true;
      },
      async saveLeadFieldValues(leadId, values) {
        const previous = data.leadFieldValues.filter((item) => item.leadId === leadId);
        const changed = Object.entries(values).filter(([fieldId, value]) => {
          const current = previous.find((item) => item.fieldId === fieldId)?.value ?? null;
          return JSON.stringify(current) !== JSON.stringify(value ?? null);
        });
        if (!changed.length) return true;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.saveLeadFieldValues(leadId, values));
            if (result.stale) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao salvar campos do lead", error);
            showFeedback("Não foi possível salvar os dados adicionais do lead. Verifique sua conexão e tente novamente.");
            throw error;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        const now = new Date().toISOString();
        const labels = changed
          .map(([fieldId]) => data.leadFields.find((field) => field.id === fieldId)?.name)
          .filter(Boolean)
          .join(", ");
        setData((d) => ({
          ...d,
          leadFieldValues: [
            ...d.leadFieldValues.filter((item) => item.leadId !== leadId || !(item.fieldId in values)),
            ...Object.entries(values)
              .filter(([, value]) => value !== null && value !== "" && !(Array.isArray(value) && value.length === 0))
              .map(([fieldId, value]) => ({ leadId, fieldId, value })),
          ],
          events: [
            ...d.events,
            {
              id: crypto.randomUUID(),
              leadId,
              type: "lead_fields_updated",
              title: "Dados atualizados",
              description: labels || "Dados adicionais atualizados",
              occurredAt: now,
              kind: "event" as const,
            },
          ],
        }));
        return true;
      },
      async addFormSection(funnelId, name) {
        const clean = name.trim();
        if (!clean) return false;
        const section: LeadFormSection = {
          id: crypto.randomUUID(), funnelId, name: clean,
          order: data.formSections.filter((item) => item.funnelId === funnelId && !item.archived).length + 1,
        };
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.addFormSection(section));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => ({ ...d, formSections: [...d.formSections, section] }));
        return true;
      },
      async updateFormSection(sectionId, patch) {
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.updateFormSection(sectionId, patch));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => ({ ...d, formSections: d.formSections.map((section) => section.id === sectionId ? { ...section, ...patch } : section) }));
        return true;
      },
      async reorderFormSections(funnelId, orderedIds) {
        const scoped = data.formSections.filter((section) => section.funnelId === funnelId && !section.archived);
        const allowed = new Set(scoped.map((section) => section.id));
        const seen = new Set<string>();
        const nextIds = orderedIds.filter((id) => {
          if (!allowed.has(id) || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        nextIds.push(...scoped.map((section) => section.id).filter((id) => !seen.has(id)));
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.saveFormSectionOrder(funnelId, nextIds));
            if (result.stale) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao reordenar seções do formulário", error);
            showFeedback("Não foi possível salvar a nova ordem das seções.");
            throw error;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        const positions = new Map(nextIds.map((id, index) => [id, index + 1]));
        setData((d) => ({
          ...d,
          formSections: [
            ...nextIds
              .map((id) => d.formSections.find((section) => section.id === id))
              .filter((section): section is LeadFormSection => Boolean(section)),
            ...d.formSections.filter((section) => !allowed.has(section.id)),
          ].map((section) => {
            const order = positions.get(section.id);
            return order === undefined ? section : { ...section, order };
          }),
        }));
        return true;
      },
      async addLeadField(input) {
        const field: LeadFieldDefinition = {
          ...input,
          id: crypto.randomUUID(),
          order: data.leadFields.filter((item) => item.sectionId === input.sectionId && !item.archived).length + 1,
        };
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.addLeadField(field));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => ({ ...d, leadFields: [...d.leadFields, field] }));
        return true;
      },
      async updateLeadField(fieldId, patch) {
        const current = data.leadFields.find((field) => field.id === fieldId);
        if (!current) return false;
        if (patch.type && patch.type !== current.type && data.leadFieldValues.some((value) => value.fieldId === fieldId)) {
          throw new Error("O tipo não pode mudar depois que o campo já possui valores.");
        }
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.updateLeadField(fieldId, patch));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => ({ ...d, leadFields: d.leadFields.map((field) => field.id === fieldId ? { ...field, ...patch } : field) }));
        return true;
      },
      async reorderLeadFields(sectionId, orderedIds) {
        const scoped = data.leadFields.filter((field) => field.sectionId === sectionId && !field.archived);
        const allowed = new Set(scoped.map((field) => field.id));
        const seen = new Set<string>();
        const nextIds = orderedIds.filter((id) => {
          if (!allowed.has(id) || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        nextIds.push(...scoped.map((field) => field.id).filter((id) => !seen.has(id)));
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.saveLeadFieldOrder(sectionId, nextIds));
            if (result.stale) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao reordenar campos do formulário", error);
            showFeedback("Não foi possível salvar a nova ordem dos campos.");
            throw error;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        const positions = new Map(nextIds.map((id, index) => [id, index + 1]));
        setData((d) => ({
          ...d,
          leadFields: [
            ...nextIds
              .map((id) => d.leadFields.find((field) => field.id === id))
              .filter((field): field is LeadFieldDefinition => Boolean(field)),
            ...d.leadFields.filter((field) => !allowed.has(field.id)),
          ].map((field) => {
            const order = positions.get(field.id);
            return order === undefined ? field : { ...field, order };
          }),
        }));
        return true;
      },
      async addLeadFieldOption(fieldId, name, color) {
        const clean = name.trim().replace(/\s+/g, " ");
        const field = data.leadFields.find((item) => item.id === fieldId);
        if (!field || !clean) throw new Error("Campo ou opção inválidos.");
        const existing = data.leadFieldOptions.find((item) => item.fieldId === fieldId && !item.archived && normalizeCatalogName(item.name) === normalizeCatalogName(clean));
        if (existing) return existing.id;
        const option: LeadFieldOption = { id: crypto.randomUUID(), fieldId, name: clean, color, order: data.leadFieldOptions.filter((item) => item.fieldId === fieldId).length + 1 };
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.addLeadFieldOption(option));
          if (result.stale) return "";
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return "";
        setData((d) => ({ ...d, leadFieldOptions: [...d.leadFieldOptions, option] }));
        return option.id;
      },
      async archiveLeadField(fieldId, archived = true) {
        const sessionToken = isDemoMode ? undefined : currentRemoteSession();
        if (sessionToken) {
          const result = await guardedRemoteCall(sessionToken, () => remote.updateLeadField(fieldId, { archived }));
          if (result.stale) return false;
        }
        if (sessionToken && !isCurrentRemoteSession(sessionToken)) return false;
        setData((d) => ({ ...d, leadFields: d.leadFields.map((field) => field.id === fieldId ? { ...field, archived } : field) }));
        return true;
      },
      archiveLead(leadId) {
        if (!isDemoMode)
          syncRemote(
            async (token) => {
              const result = await guardedRemoteCall(token, () => remote.archive("leads", leadId, true));
              if (!result.stale && result.value.error) throw result.value.error;
            },
            "Não foi possível mover o lead para a lixeira.",
          );
        const archivedAt = new Date().toISOString();
        setData((d) => ({
          ...d,
          leads: d.leads.map((l) =>
            l.id === leadId ? { ...l, archived: true, archivedAt } : l,
          ),
        }));
      },
      restoreLead(leadId) {
        if (!isDemoMode)
          syncRemote(
            async (token) => {
              const result = await guardedRemoteCall(token, () => remote.archive("leads", leadId, false));
              if (!result.stale && result.value.error) throw result.value.error;
            },
            "Não foi possível restaurar o lead.",
          );
        setData((d) => ({
          ...d,
          leads: d.leads.map((l) =>
            l.id === leadId ? { ...l, archived: false, archivedAt: undefined } : l,
          ),
        }));
      },
      async deleteLeadPermanently(leadId) {
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.deleteLeadPermanently(leadId));
            if (result.stale) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao excluir lead permanentemente", error);
            showFeedback(
              "Não foi possível excluir este lead permanentemente. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        setData((d) => ({
          ...d,
          leads: d.leads.filter((l) => l.id !== leadId),
          memberships: d.memberships.filter((m) => m.leadId !== leadId),
          tasks: d.tasks.filter((t) => t.leadId !== leadId),
          events: d.events.filter((e) => e.leadId !== leadId),
          attachments: d.attachments.filter((a) => a.leadId !== leadId),
          links: d.links.filter((l) => l.leadId !== leadId),
        }));
        return true;
      },
      createTask(task, options) {
        return runCreateTask(task, options);
      },
      addTask(task, options) {
        return runCreateTask(task, options);
      },
      /*
        const created = {
          ...task,
          id: crypto.randomUUID(),
          status: "pending" as const,
        };
        if (!isDemoMode)
          syncRemote(
            remote.addTask(created),
            "Não foi possível criar a tarefa.",
          );
        setData((d) => ({
          ...d,
          tasks: [created, ...d.tasks],
          events: [
            ...d.events,
            {
              id: crypto.randomUUID(),
              leadId: task.leadId,
              type: "task_created",
              title: task.description,
              occurredAt: new Date().toISOString(),
              kind: "event",
            },
          ],
        }));
      },
      */
      addEvent(leadId, title, type = "manual_event", description) {
        const event = {
          id: crypto.randomUUID(),
          leadId,
          type,
          title,
          description,
          occurredAt: new Date().toISOString(),
          kind: "event" as const,
        };
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.addEvent(event)),
            "Não foi possível registrar o acontecimento.",
          );
        setData((d) => ({
          ...d,
          events: [...d.events, event],
          ...(countsAsInteraction(type) ? {
            leads: d.leads.map((lead) => lead.id === leadId ? { ...lead, lastInteractionAt: event.occurredAt } : lead),
          } : {}),
        }));
      },
      async addAttachment(leadId, file) {
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.uploadAttachment(leadId, file));
            if (result.stale) return false;
            if (!isCurrentRemoteSession(sessionToken)) return false;
            setData((d) => ({ ...d, attachments: [result.value, ...d.attachments] }));
            return true;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao anexar arquivo", error);
            showFeedback(
              "Não foi possível anexar o arquivo. Verifique sua conexão e tente novamente.",
            );
            return false;
          }
        }
        return new Promise<boolean>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            setData((d) => ({
              ...d,
              attachments: [
                {
                  id: crypto.randomUUID(),
                  leadId,
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  dataUrl: String(reader.result),
                  createdAt: new Date().toISOString(),
                },
                ...d.attachments,
              ],
              events: [
                ...d.events,
                {
                  id: crypto.randomUUID(),
                  leadId,
                  type: "file_attached",
                  title: "Arquivo anexado",
                  description: file.name,
                  occurredAt: new Date().toISOString(),
                  kind: "event",
                },
              ],
            }));
            resolve(true);
          };
          reader.onerror = () => reject(reader.error ?? new Error("Não foi possível ler o arquivo"));
          reader.readAsDataURL(file);
        });
      },
      async removeAttachment(attachmentId) {
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.deleteAttachment(attachmentId));
            if (result.stale) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao excluir anexo", error);
            showFeedback(
              "Não foi possível excluir este anexo. Verifique sua conexão e tente novamente.",
            );
            return false;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        setData((d) => ({
          ...d,
          attachments: d.attachments.filter((a) => a.id !== attachmentId),
        }));
        return true;
      },
      addLink(leadId, label, url) {
        const link = {
          id: crypto.randomUUID(),
          leadId,
          label,
          url,
          createdAt: new Date().toISOString(),
        };
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.addLink(link)),
            "Não foi possível salvar o link.",
          );
        setData((d) => ({
          ...d,
          links: [link, ...d.links],
          events: [
            ...d.events,
            {
              id: crypto.randomUUID(),
              leadId,
              type: "link_added",
              title: "Link adicionado",
              description: label,
              occurredAt: new Date().toISOString(),
              kind: "event",
            },
          ],
        }));
      },
      addSavedView(view) {
        const saved = { ...view, id: crypto.randomUUID() };
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.saveView(saved)),
            "Não foi possível salvar a visualização.",
          );
        setData((d) => ({ ...d, savedViews: [...d.savedViews, saved] }));
      },
      async updateFunnel(funnelId, patch) {
        const name = patch.name.trim().replace(/\s+/g, " ");
        const description = patch.description.trim();
        if (!name) return false;
        if (!data.funnels.some((funnel) => funnel.id === funnelId)) return false;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.updateFunnel(funnelId, { name, description }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao atualizar funil", error);
            showFeedback(
              "Não foi possível salvar o funil. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => ({
          ...d,
          funnels: d.funnels.map((funnel) =>
            funnel.id === funnelId ? { ...funnel, name, description } : funnel,
          ),
        }));
        return true;
      },
      async renameStage(stageId, name) {
        const clean = name.trim();
        if (!clean) return false;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.renameStage(stageId, clean));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao renomear etapa", error);
            showFeedback(
              "Não foi possível renomear a etapa. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => ({
          ...d,
          stages: d.stages.map((stage) =>
            stage.id === stageId ? { ...stage, name: clean } : stage,
          ),
        }));
        return true;
      },
      async updateConfigColor(kind, id, color) {
        if (!/^#[0-9a-f]{6}$/i.test(color)) return false;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.updateConfigColor(kind, id, color));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao salvar cor da configuração", error);
            showFeedback(
              "Não foi possível salvar a cor. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) =>
          kind === "stage"
            ? {
                ...d,
                stages: d.stages.map((item) =>
                  item.id === id ? { ...item, color } : item,
                ),
              }
            : kind === "tag"
              ? {
                  ...d,
                  tags: d.tags.map((item) =>
                    item.id === id ? { ...item, color } : item,
                  ),
                }
              : {
                  ...d,
                  triggers: d.triggers.map((item) =>
                    item.id === id ? { ...item, color } : item,
                  ),
                },
        );
        return true;
      },
      async reorderConfig(kind, orderedIds, parentId) {
        if (kind === "stage" && !parentId) return false;
        const scoped =
          kind === "funnel"
            ? data.funnels
            : kind === "stage"
              ? data.stages.filter((item) => item.funnelId === parentId)
              : kind === "tag"
                ? data.tags
                : kind === "trigger"
                  ? data.triggers
                  : data.lossReasons;
        const scopedIds = new Set(scoped.map((item) => item.id));
        const seen = new Set<string>();
        const nextIds = orderedIds.filter((id) => {
          if (!scopedIds.has(id) || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        nextIds.push(...scoped.map((item) => item.id).filter((id) => !seen.has(id)));
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.saveConfigOrder(kind, nextIds, parentId));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao reordenar configuração", error);
            showFeedback(
              "Não foi possível salvar a nova ordem. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        const positions = new Map(nextIds.map((id, index) => [id, index + 1]));
        setData((d) => {
          const update = <T extends { id: string; order?: number }>(item: T) =>
            positions.has(item.id)
              ? { ...item, order: positions.get(item.id) }
              : item;
          const reorderAll = <T extends { id: string; order?: number }>(items: T[]) =>
            nextIds
              .map((id) => items.find((item) => item.id === id))
              .filter((item): item is T => Boolean(item))
              .map(update);
          if (kind === "funnel") return { ...d, funnels: reorderAll(d.funnels) };
          if (kind === "stage") {
            const scopedItems = d.stages.filter((item) => item.funnelId === parentId);
            return {
              ...d,
              stages: [...reorderAll(scopedItems), ...d.stages.filter((item) => item.funnelId !== parentId)],
            };
          }
          if (kind === "tag") return { ...d, tags: reorderAll(d.tags) };
          if (kind === "trigger") return { ...d, triggers: reorderAll(d.triggers) };
          return { ...d, lossReasons: reorderAll(d.lossReasons) };
        });
        return true;
      },
      deleteSavedView(viewId) {
        if (!isDemoMode)
          syncRemote(
            (token) => guardedRemoteCall(token, () => remote.deleteView(viewId)),
            "Não foi possível excluir a visualização.",
          );
        setData((d) => ({
          ...d,
          savedViews: d.savedViews.filter((v) => v.id !== viewId),
        }));
      },
      async addFunnel(name, description) {
        const item = {
          id: crypto.randomUUID(),
          name,
          description,
          order: data.funnels.length + 1,
          // Um funil novo nasce em grafo: o editor aceita condição, reunião,
          // cobrança e transferência, como o processo do protótipo.
          structureType: "graph",
        } as Funnel;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.addConfig("funnels", {
              id: item.id,
              name,
              description,
              position: data.funnels.length + 1,
            }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao criar funil", error);
            showFeedback(
              "Não foi possível criar o funil. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => ({ ...d, funnels: [...d.funnels, item] }));
        return true;
      },
      async createFunnel(input) {
        const name = input.name.trim().replace(/\s+/g, " ");
        const description = input.description.trim();
        const names = input.stages.map((item) => item.trim().replace(/\s+/g, " ")).filter(Boolean);
        if (!name || names.length === 0) return false;
        const funnel: Funnel = { id: crypto.randomUUID(), name, description, order: data.funnels.length + 1 };
        const stages: Stage[] = names.map((stageName, index) => ({
          id: crypto.randomUUID(),
          funnelId: funnel.id,
          name: stageName,
          order: index + 1,
          color: STAGE_COLORS[(index + 1) % STAGE_COLORS.length],
        }));
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          const persistConfig = async (table: string, row: Record<string, unknown>) => {
            const result = await guardedRemoteCall(sessionToken, () => remote.addConfig(table, row));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
            return true;
          };
          try {
            if (!await persistConfig("funnels", { id: funnel.id, name, description, position: funnel.order })) return false;
            for (const stage of stages)
              if (!await persistConfig("stages", { id: stage.id, funnel_id: funnel.id, name: stage.name, position: stage.order, color: stage.color })) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao criar funil", error);
            showFeedback("Não foi possível criar o funil. Verifique sua conexão e tente novamente.");
            throw error;
          }
        }
        setData((current) => ({ ...current, funnels: [...current.funnels, funnel], stages: [...current.stages, ...stages] }));
        return funnel.id;
      },
      async saveFunnelStages(funnelId, input) {
        const funnel = data.funnels.find((item) => item.id === funnelId && !item.archived);
        if (!funnel) return false;
        const current = data.stages.filter((item) => item.funnelId === funnelId && !item.archived);
        const byId = new Map(current.map((item) => [item.id, item]));
        const rows = input.rows
          .map((row) => ({ id: row.id, name: row.name.trim().replace(/\s+/g, " ") }))
          .filter((row) => row.name && (!row.id || byId.has(row.id)));
        if (rows.length === 0) return false;
        const removed = input.removedIds.filter((id) => byId.has(id) && !rows.some((row) => row.id === id));
        const renamed = rows.filter((row): row is { id: string; name: string } => Boolean(row.id) && byId.get(row.id!)!.name !== row.name);
        const created: Stage[] = rows
          .filter((row) => !row.id)
          .map((row, index) => ({
            id: crypto.randomUUID(),
            funnelId,
            name: row.name,
            order: current.length + index + 1,
            color: STAGE_COLORS[(current.length + index + 1) % STAGE_COLORS.length],
          }));
        let createdIndex = 0;
        const orderedIds = rows.map((row) => row.id ?? created[createdIndex++].id);
        const archivedIds = data.stages.filter((item) => item.funnelId === funnelId && item.archived).map((item) => item.id);
        const orderChanged = orderedIds.some((id, index) => current[index]?.id !== id);
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          const guarded = async <T,>(operation: () => Promise<T>) => {
            const result = await guardedRemoteCall(sessionToken, operation);
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return null;
            return result;
          };
          try {
            for (const row of renamed) if (!await guarded(() => remote.renameStage(row.id, row.name))) return false;
            for (const stage of created) {
              const result = await guarded(() => remote.addConfig("stages", { id: stage.id, funnel_id: funnelId, name: stage.name, position: stage.order, color: stage.color }));
              if (!result) return false;
              if (result.value.error) throw result.value.error;
            }
            for (const id of removed) {
              const result = await guarded(() => remote.archive("stages", id, true));
              if (!result) return false;
              if (result.value.error) throw result.value.error;
            }
            if (orderChanged || created.length || removed.length)
              if (!await guarded(() => remote.saveConfigOrder("stage", [...orderedIds, ...removed, ...archivedIds], funnelId))) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao salvar etapas", error);
            showFeedback("Não foi possível salvar as etapas. Verifique sua conexão e tente novamente.");
            throw error;
          }
        }
        const positions = new Map([...orderedIds, ...removed, ...archivedIds].map((id, index) => [id, index + 1]));
        const renamedNames = new Map(renamed.map((row) => [row.id, row.name]));
        const removedSet = new Set(removed);
        setData((d) => ({
          ...d,
          stages: [
            ...d.stages.map((stage) => {
              if (stage.funnelId !== funnelId) return stage;
              return {
                ...stage,
                name: renamedNames.get(stage.id) ?? stage.name,
                order: positions.get(stage.id) ?? stage.order,
                archived: removedSet.has(stage.id) ? true : stage.archived,
              };
            }),
            ...created.map((stage) => ({ ...stage, order: positions.get(stage.id) ?? stage.order })),
          ],
        }));
        return true;
      },
      async duplicateFunnel(sourceFunnelId, name) {
        const source = data.funnels.find((item) => item.id === sourceFunnelId && !item.archived);
        const cleanName = name.trim().replace(/\s+/g, " ");
        if (!source || !cleanName) throw new Error("Funil de origem ou nome inválido.");
        const funnel: Funnel = { id: crypto.randomUUID(), name: cleanName, description: source.description, order: data.funnels.length + 1 };
        const stages = data.stages.filter((item) => item.funnelId === source.id).map((item) => ({ ...item, id: crypto.randomUUID(), funnelId: funnel.id }));
        const tags = data.tags.filter((item) => item.funnelId === source.id).map((item) => ({ ...item, id: crypto.randomUUID(), funnelId: funnel.id }));
        const triggers = data.triggers.filter((item) => item.funnelId === source.id).map((item) => ({ ...item, id: crypto.randomUUID(), funnelId: funnel.id }));
        const lossReasons = data.lossReasons.filter((item) => item.funnelId === source.id).map((item) => ({ ...item, id: crypto.randomUUID(), funnelId: funnel.id }));
        const sectionIdMap = new Map<string, string>();
        const formSections = data.formSections.filter((item) => item.funnelId === source.id).map((item) => { const id = crypto.randomUUID(); sectionIdMap.set(item.id, id); return { ...item, id, funnelId: funnel.id }; });
        const fieldIdMap = new Map<string, string>();
        const leadFields = data.leadFields.filter((item) => item.funnelId === source.id).map((item) => { const id = crypto.randomUUID(); fieldIdMap.set(item.id, id); return { ...item, id, funnelId: funnel.id, sectionId: sectionIdMap.get(item.sectionId) ?? item.sectionId }; });
        const leadFieldOptions = data.leadFieldOptions.filter((item) => fieldIdMap.has(item.fieldId)).map((item) => ({ ...item, id: crypto.randomUUID(), fieldId: fieldIdMap.get(item.fieldId)! }));
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          const persistConfig = async (table: string, row: Record<string, unknown>) => {
            const result = await guardedRemoteCall(sessionToken, () => remote.addConfig(table, row));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
            return true;
          };
          const persistPlain = async (operation: () => Promise<unknown>) => {
            const result = await guardedRemoteCall(sessionToken, operation);
            return !result.stale && isCurrentRemoteSession(sessionToken);
          };
          if (!await persistConfig("funnels", { id: funnel.id, name: funnel.name, description: funnel.description, position: funnel.order })) return false;
          for (const stage of stages) if (!await persistConfig("stages", { id: stage.id, funnel_id: funnel.id, name: stage.name, position: stage.order, color: stage.color })) return false;
          for (const tag of tags) if (!await persistConfig("tags", { id: tag.id, funnel_id: funnel.id, name: tag.name, color: tag.color })) return false;
          for (const trigger of triggers) if (!await persistConfig("triggers", { id: trigger.id, funnel_id: funnel.id, name: trigger.name, color: trigger.color })) return false;
          for (const reason of lossReasons) if (!await persistConfig("loss_reasons", { id: reason.id, funnel_id: funnel.id, name: reason.name, position: reason.order })) return false;
          for (const section of formSections) if (!await persistPlain(() => remote.addFormSection(section))) return false;
          for (const field of leadFields) if (!await persistPlain(() => remote.addLeadField(field))) return false;
          for (const option of leadFieldOptions) if (!await persistPlain(() => remote.addLeadFieldOption(option))) return false;
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        setData((current) => ({ ...current, funnels: [...current.funnels, funnel], stages: [...current.stages, ...stages], tags: [...current.tags, ...tags], triggers: [...current.triggers, ...triggers], lossReasons: [...current.lossReasons, ...lossReasons], formSections: [...current.formSections, ...formSections], leadFields: [...current.leadFields, ...leadFields], leadFieldOptions: [...current.leadFieldOptions, ...leadFieldOptions] }));
        return true;
      },
      async addStage(funnelId, name) {
        const order =
            data.stages.filter((x) => x.funnelId === funnelId).length + 1,
          item = {
            id: crypto.randomUUID(),
            funnelId,
            name,
            order,
            color: STAGE_COLORS[order % STAGE_COLORS.length],
          } as Stage;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.addConfig("stages", {
              id: item.id,
              funnel_id: funnelId,
              name,
              position: order,
              color: item.color,
            }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao criar etapa", error);
            showFeedback(
              "Não foi possível criar a etapa. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => ({ ...d, stages: [...d.stages, item] }));
        return true;
      },
      async addTag(name, color = "#8e85ff", funnelId = data.funnels.find((funnel) => !funnel.archived)?.id ?? "") {
        const item = { id: crypto.randomUUID(), funnelId, name, color, order: data.tags.filter((tag) => tag.funnelId === funnelId).length + 1 } as Tag;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            // A migration nova atribui position por trigger; omitir a coluna
            // mantém a criação compatível enquanto o ambiente remoto é atualizado.
            const result = await remoteMutation(sessionToken, () => remote.addConfig("tags", {
              id: item.id,
              funnel_id: funnelId,
              name: item.name,
              color: item.color,
            }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao criar tag", error);
            showFeedback(
              "Não foi possível criar a tag. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => ({ ...d, tags: [...d.tags, item] }));
        return true;
      },
      async createTag(name, color = "#8e85ff", funnelId = data.funnels.find((funnel) => !funnel.archived)?.id ?? "") {
        const clean = name.trim().replace(/\s+/g, " ");
        const existing = data.tags.find(
          (item) => item.funnelId === funnelId && !item.archived && normalizeCatalogName(item.name) === normalizeCatalogName(clean),
        );
        if (existing) return existing.id;
        const item = { id: crypto.randomUUID(), funnelId, name: clean, color, order: data.tags.filter((tag) => tag.funnelId === funnelId).length + 1 } as Tag;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.addConfig("tags", {
              id: item.id,
              funnel_id: funnelId,
              name: item.name,
              color: item.color,
            }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return "";
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return "";
            throw error;
          }
        }
        setData((d) => ({ ...d, tags: [...d.tags, item] }));
        return item.id;
      },
      async addTrigger(name, color = "#f5b860", funnelId = data.funnels.find((funnel) => !funnel.archived)?.id ?? "") {
        const clean = name.trim().replace(/\s+/g, " ");
        if (!clean) return false;
        const existing = data.triggers.find(
          (item) => item.funnelId === funnelId && !item.archived && normalizeCatalogName(item.name) === normalizeCatalogName(clean),
        );
        if (existing) return false;
        const item = { id: crypto.randomUUID(), funnelId, name: clean, color, order: data.triggers.filter((trigger) => trigger.funnelId === funnelId).length + 1 } as Trigger;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          const result = await remoteMutation(sessionToken, () => remote.addConfig("triggers", {
            id: item.id,
            funnel_id: funnelId,
            name: item.name,
            color: item.color,
          }));
          if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
          if (result.value.error) throw result.value.error;
        }
        setData((d) => ({ ...d, triggers: [...d.triggers, item] }));
        return true;
      },
      async createTrigger(name, color = "#f5b860", funnelId = data.funnels.find((funnel) => !funnel.archived)?.id ?? "") {
        const clean = name.trim().replace(/\s+/g, " ");
        const existing = data.triggers.find(
          (item) => item.funnelId === funnelId && !item.archived && normalizeCatalogName(item.name) === normalizeCatalogName(clean),
        );
        if (existing) return existing.id;
        const item = { id: crypto.randomUUID(), funnelId, name: clean, color, order: data.triggers.filter((trigger) => trigger.funnelId === funnelId).length + 1 } as Trigger;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.addConfig("triggers", {
              id: item.id,
              funnel_id: funnelId,
              name: item.name,
              color: item.color,
            }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return "";
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return "";
            throw error;
          }
        }
        setData((d) => ({ ...d, triggers: [...d.triggers, item] }));
        return item.id;
      },
      async addLossReason(name, funnelId = data.funnels.find((funnel) => !funnel.archived)?.id ?? "") {
        const item = {
          id: crypto.randomUUID(),
          funnelId,
          name,
          order: data.lossReasons.filter((reason) => reason.funnelId === funnelId).length + 1,
        } as LossReason;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.addConfig("loss_reasons", {
              id: item.id,
              funnel_id: funnelId,
              name,
              position: item.order,
            }));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao criar motivo de perda", error);
            showFeedback(
              "Não foi possível criar o motivo de perda. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => ({ ...d, lossReasons: [...d.lossReasons, item] }));
        return true;
      },
      async renameConfig(kind, id, name) {
        const clean = name.trim();
        if (!clean) return false;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.renameConfig(kind, id, clean));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao renomear configuração", error);
            showFeedback(
              "Não foi possível renomear esta configuração. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) =>
          kind === "tag"
            ? {
                ...d,
                tags: d.tags.map((item) =>
                  item.id === id ? { ...item, name: clean } : item,
                ),
              }
            : kind === "trigger"
              ? {
                  ...d,
                  triggers: d.triggers.map((item) =>
                    item.id === id ? { ...item, name: clean } : item,
                  ),
                }
            : {
                ...d,
                lossReasons: d.lossReasons.map((item) =>
                  item.id === id ? { ...item, name: clean } : item,
                ),
              },
        );
        return true;
      },
      async addMembership(leadId, funnelId, stageId) {
        if (
          data.memberships.some(
            (m) => m.leadId === leadId && m.funnelId === funnelId,
          )
        )
          return false;
        const now = new Date().toISOString(),
          stage = data.stages.find((s) => s.id === stageId);
        let id: string = crypto.randomUUID();
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await guardedRemoteCall(sessionToken, () => remote.createMembership(leadId, funnelId, stageId));
            if (result.stale) return false;
            id = result.value;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao criar participação", error);
            showFeedback(
              "Não foi possível adicionar o lead a este funil. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
          if (!isCurrentRemoteSession(sessionToken)) return false;
        }
        setData((d) => ({
          ...d,
          memberships: [
            ...d.memberships,
            {
              id,
              leadId,
              funnelId,
              stageId,
              status: "active",
              enteredStageAt: now,
            },
          ],
          events: [
            ...d.events,
            {
              id: crypto.randomUUID(),
              leadId,
              membershipId: id,
              type: "membership_created",
              title: `Participação criada em ${stage?.name ?? "funil"}`,
              occurredAt: now,
              kind: "stage",
              stageId,
            },
          ],
        }));
        return true;
      },
      async toggleArchivedConfig(kind, id) {
        const table =
            kind === "funnel"
              ? "funnels"
              : kind === "stage"
                ? "stages"
                : kind === "tag"
                  ? "tags"
                  : kind === "trigger"
                    ? "triggers"
                    : "loss_reasons",
          list =
            kind === "funnel"
              ? data.funnels
              : kind === "stage"
                ? data.stages
                : kind === "tag"
                  ? data.tags
                  : kind === "trigger"
                    ? data.triggers
                    : data.lossReasons,
          current = list.find((x) => x.id === id);
        if (!current) return false;
        const archived = !current.archived;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.archive(table, id, archived));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error(
              "Falha ao alterar arquivamento da configuração",
              error,
            );
            showFeedback(
              "Não foi possível atualizar a lixeira. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => {
          if (kind === "funnel")
            return {
              ...d,
              funnels: d.funnels.map((x) =>
                x.id === id ? { ...x, archived } : x,
              ),
            };
          if (kind === "stage")
            return {
              ...d,
              stages: d.stages.map((x) =>
                x.id === id ? { ...x, archived } : x,
              ),
            };
          if (kind === "tag")
            return {
              ...d,
              tags: d.tags.map((x) => (x.id === id ? { ...x, archived } : x)),
            };
          if (kind === "trigger")
            return {
              ...d,
              triggers: d.triggers.map((x) => (x.id === id ? { ...x, archived } : x)),
            };
          return {
            ...d,
            lossReasons: d.lossReasons.map((x) =>
              x.id === id ? { ...x, archived } : x,
            ),
          };
        });
        return true;
      },
      async removeConfig(kind, id) {
        const currentList =
          kind === "funnel"
            ? data.funnels
            : kind === "stage"
              ? data.stages
              : kind === "tag"
                ? data.tags
                : kind === "trigger"
                  ? data.triggers
                  : data.lossReasons;
        const current = currentList.find((item) => item.id === id);
        if (!current) return false;
        const table =
          kind === "lossReason"
            ? "loss_reasons"
            : kind === "funnel"
              ? "funnels"
              : kind === "trigger"
                ? "triggers"
              : `${kind}s`;
        if (!isDemoMode) {
          const sessionToken = currentRemoteSession();
          try {
            const result = await remoteMutation(sessionToken, () => remote.archive(table, id, true));
            if (result.stale || !isCurrentRemoteSession(sessionToken)) return false;
            if (result.value.error) throw result.value.error;
          } catch (error) {
            if (!isCurrentRemoteSession(sessionToken)) return false;
            console.error("Falha ao remover configuração", error);
            showFeedback(
              "Não foi possível enviar esta configuração para a lixeira. Verifique sua conexão e tente novamente.",
            );
            throw error;
          }
        }
        setData((d) => {
          if (kind === "funnel")
            return {
              ...d,
              funnels: d.funnels.map((item) =>
                item.id === id ? { ...item, archived: true } : item,
              ),
            };
          if (kind === "stage")
            return {
              ...d,
              stages: d.stages.map((item) =>
                item.id === id ? { ...item, archived: true } : item,
              ),
            };
          if (kind === "tag")
            return {
              ...d,
              tags: d.tags.map((item) =>
                item.id === id ? { ...item, archived: true } : item,
              ),
            };
          if (kind === "trigger")
            return {
              ...d,
              triggers: d.triggers.map((item) =>
                item.id === id ? { ...item, archived: true } : item,
              ),
            };
          return {
            ...d,
            lossReasons: d.lossReasons.map((item) =>
              item.id === id ? { ...item, archived: true } : item,
            ),
          };
        });
        return true;
      },
      resetDemo() {
        const reset = normalizeTransversalData(
          structuredClone(demoData),
          dataRef.current.demoOwnerId ?? `demo:${crypto.randomUUID()}`,
        );
        dataRef.current = reset;
        identityAdapterRef.current?.syncExternal(reset);
        taskAdapterRef.current?.syncExternal(taskStateFromData(reset));
        communicationAdapterRef.current?.syncExternal(communicationStateFromData(reset));
        aiAdapterRef.current?.syncExternal(aiStateFromData(reset));
        setData(reset);
      },
    };
  }, [data]);
  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("StoreProvider ausente");
  return ctx;
}
